/**
 * admin 命名空间字典（i18n 分区）
 *
 * 从原 translations.ts 主字典按 key 首个点分段拆出；本文件内 key 不带命名空间前缀，
 * 调用点写 t('admin:key')。字段内容与拆分前逐字节一致。
 */

import type { TranslationDict } from '../types'

export const adminZh: TranslationDict = {

  // ======================== 管理员面板 / Admin ========================
  'title': '管理员面板',
  'subtitle': '系统管理与监控 ·',
  'backToList': '返回列表',
  'backToApp': '返回应用',
  'collapseRail': '收起导航',
  'expandRail': '展开导航',
  'backToMe': '返回我的',

  // 管理面板分组：按「管理对象」分桶，对齐 GitHub / Vercel / Google 管理控制台的分组习惯
  'groupGeneral': '通用',
  'groupPeople': '用户与内容',
  'groupCapabilities': '能力与扩展',
  'groupMonitoring': '监控与日志',
  'groupData': '数据与维护',

  // 页签文案：标签 + 移动端列表说明
  'capabilities': '插件与技能',
  'capabilitiesPlugins': '插件',
  'capabilitiesDesc': '插件、工具、技能与技能背包',
  'prompt': '系统提示词',
  'promptDesc': '查看和自定义发给 AI 的系统提示词段',
  'store': '商城',
  'storeDesc': '插件商城、已安装与商城同步源（世界商城在 /market 对普通用户开放）',
  'overviewDesc': '站点规模与维护模式',
  'usersDesc': '账号、角色与管理权限',
  'agentsDesc': '全站 AI 角色与自修改开关',
  'groupsDesc': '群聊审查与解散',
  'codesDesc': '生成与查看兑换码',
  'apikeyPoolDesc': '共享 API 密钥池与调用统计',
  'opencliDesc': '命令行桥接的配置、授权与日志',
  'federationDesc': '与其他站点的互通与联邦关系',
  'metricsDesc': 'CPU、内存、磁盘等实时监控',
  'usageDesc': '模型调用与额度消耗分析',
  'convlogDesc': '全部会话的消息记录检索',
  'auditDesc': '管理员操作审计',
  'backupDesc': '数据库与文件的导出和恢复',
  'totalUsers': '总用户数',
  'totalAgents': '总 AI 数',
  'totalGroups': '总群聊数',
  'total': '总计',
  'pendingRequests': '待处理申请',
  'usersColId': 'ID',
  'usersColUsername': '用户名',
  'usersColRole': '角色',
  'usersColQuota': '额度',
  'usersColStatus': '状态',
  'usersColAction': '操作',
  'active': '正常',
  'banned': '已封禁',
  'ban': '封禁',
  'unban': '解封',
  'confirmPromote': '确定将 {username} 提升为管理员？',
  'promote': '提升',
  'confirmDemote': '确定撤销 {username} 的管理员权限？',
  'demote': '降级',
  'resetPassword': '重置密码',
  'resetPasswordTitle': '重置 {username} 的密码',
  'resetPasswordPlaceholder': '输入新密码（至少6位）',
  'resetPasswordSuccess': '密码已重置',
  'resetPasswordError': '重置密码失败',
  'agentsColId': 'ID',
  'agentsColName': '名称',
  'agentsColOwner': '所属用户',
  'agentsColStatus': '状态',
  'agentsColSelfEdit': '自修改',
  'agentsColAction': '操作',
  'searchAiPlaceholder': '搜索 AI 名称...',
  'disableSelfEdit': '关闭自修改',
  'enableSelfEdit': '开启自修改',
  'groupsColId': 'ID',
  'groupsColName': '名称',
  'groupsColOwner': '群主',
  'groupsColVector': '向量加速',
  'groupsColAction': '操作',
  'enabled': '已开启',
  'notEnabled': '未启用',
  'disabled': '未开启',
  'confirmDismissGroup': '确定解散此群聊？',
  'dismiss': '解散',
  'creditApi': '通用API额度',
  'creditBundle': 'AI包断额度',
  'creditFile': '文件存储配额',
  'generateFailed': '生成失败，请检查参数',
  'generateCode': '生成兑换码',
  'codeType': '类型',
  'expiresDays': '有效期（天）',
  'isApiPool': 'API 池额度',
  'generating': '生成中...',
  'generate': '生成',
  'codeNote': '备注（管理员可见）',
  'codeNotePlaceholder': '例：给张三的 API 额度',
  'maxUsageLabel': '单码最大用量（空=一次性全额）',
  'balanceHint': '1 余额 = 10,000 tokens',
  'codeOneTimeWarning': '请复制保管，此码仅显示一次',
  'codeList': '兑换码列表',
  'codesColCode': '兑换码',
  'codesColType': '类型',
  'codesColAmount': '额度',
  'codesColNote': '备注',
  'codesColExpires': '到期',
  'codesColStatus': '状态',
  'pool': '池',
  'codeTypeDefault': 'AI创建额度',
  'usedBy': '已使用',
  'available': '可用',
  'enable': '开启',
  'disable': '关闭',
  'delete': '删除',
  'save': '保存',
  'cancel': '取消',
  'saving': '保存中...',
  'saveSuccess': '保存成功',
  'saveFailed': '保存失败',
  // 系统设置 / 系统提示词两个页签的即时提示（以前是硬编码中文，且靠 includes('失败') 判语气）
  'updateFailed': '更新失败',
  'uploadLimitUpdated': '上传大小限制已更新',
  'avatarLimitUpdated': '头像大小限制已更新',
  'bulkConcurrencyConfirm': '确定将所有群的 AI 并发数设为 {n}？此操作不可撤销。',
  'bulkConcurrencySet': '已将所有群并发数设为 {n}',
  'bulkUpdateFailed': '批量修改失败',
  'bulkApplyRunning': '执行中...',
  'bulkApply': '批量应用',
  'promptResetDone': '已恢复默认',
  'promptResetFailed': '重置失败',
  'promptOrderSaved': '顺序已保存',
  'promptOrderSaveFailed': '保存顺序失败',
  'allUsers': '所有用户',
  'operationFailed': '操作失败',
  'deleteFailed': '删除失败',
  'loadFailed': '加载失败',
  'downloadFailed': '下载失败',
  'dbBackupSuccess': '✓ 数据库备份下载成功',
  'fullBackupSuccess': '✓ 完整备份下载成功（数据库 + 所有文件）',
  'restoreFailed': '恢复失败',
  'dbRestoreSuccess': '✓ 数据库已恢复，请刷新页面',
  'dbRestoreRestart': '✓ 数据库已恢复，请重启应用生效',
  'fullRestoreSuccess': '✓ 完整备份已恢复：数据库 + 所有文件，请刷新页面',
  'fullRestoreRestart': '✓ 完整备份已恢复：数据库 + 所有文件，请重启应用生效',
  'presetsAddComplete': '预设命令添加完成',
  'presetsAddFailed': '添加预设失败',
  'exportTitle': '数据导出',
  'exportDesc': '两种备份格式，按需选择。',
  'fullBackup': '完整备份（推荐）',
  'fullBackupDesc': '导出 .tar.gz 压缩包，包含：全部数据库数据 + /app/data/ 下所有文件（AI 文件、附件、头像等）。可用于完整迁移。',
  'packing': '正在打包...',
  'downloadFullBackup': '下载完整备份',
  'dbOnly': '仅数据库',
  'dbOnlyDesc': '导出 .sql 文件，仅含数据表结构及内容。体积较小，但不含上传的附件和 AI 文件。',
  'exporting': '正在导出...',
  'downloadDbOnly': '下载仅数据库',
  'restoreTitle': '数据恢复',
  'restoreWarning': '恢复将覆盖当前所有数据。请确认备份文件正确无误。',
  'currentDbBackend': '当前数据库',
  'backupFileExt': '备份文件扩展名',
  'fullRestore': '完整恢复',
  'fullRestoreDesc': '上传 .tar.gz 完整备份，恢复数据库 + 全部文件。',
  'restoringFull': '正在完整恢复...',
  'dbRestore': '仅数据库恢复',
  'dbRestoreDesc': '上传 .sql 文件，仅恢复数据库。',
  'restoring': '正在恢复...',
  'logsColTime': '时间',
  'logsColType': '类型',
  'logsColOperator': '操作者',
  'logsColTarget': '目标',
  'logsColDetail': '详情',
  'logsColResult': '结果',
  'logsResultOk': '成功',
  'logsResultFailed': '失败',
  'logsAnonymous': '匿名',
  'logsFilterType': '类型',
  'logsFilterAll': '全部',
  'logsTypeLogin': '登录',
  'logsTypeLoginFailed': '登录失败',
  'logsTypeRegister': '注册',
  'opsTab': '运维总览',
  'opsTabDesc': '一屏数字：登录、拦截、对话、新用户',
  'opsRangeLabel': '统计范围',
  'opsRange1': '近 24 小时',
  'opsRange7': '近 7 天',
  'opsRange30': '近 30 天',
  'opsLoginOk': '登录成功',
  'opsLoginFail': '登录失败',
  'opsSuspects': '疑似攻击来源',
  'opsBlocked': '已拦截',
  'opsTurns': '对话轮次',
  'opsNewUsers': '新用户',
  'opsLoginOkValue': '{n} 次',
  'opsLoginFailValue': '{n} 次',
  'opsSuspectsValue': '{n} 个',
  'opsBlockedValue': '{n} 次',
  'opsTurnsValue': '{n} 轮',
  'opsNewUsersValue': '{n} 个',
  'opsSubAccounts': '{n} 个账号',
  'opsSubTop': '最多一个来源失败 {n} 次',
  'opsNoSuspects': '没有可疑来源',
  'opsSubFailLocked': '其中 {n} 次打满阈值被锁',
  'opsSubRepeat': '{p}% 的失败来自反复失败（2 次以上）的来源',
  'opsSubBlocked': '锁定挡回 {a} 次 · 限流挡回 {b} 次',
  'opsSubBlockedPartial': '锁定挡回 {a} 次 · 限流挡回 —（本次启动以来 {b} 次）',
  'opsSubMessages': '含 {n} 条消息',
  'opsSubTotalUsers': '当前共 {n} 个账号',
  'opsTipLoginOk': '口径：范围内成功登录条数；来源：审计日志 system_logs 的 login。点卡片看明细。',
  'opsTipLoginFail': '口径：真的输错密码的次数（被锁定挡回的不算，见「已拦截」）；来源：system_logs 的 login_failed。点卡片看明细。',
  'opsTipSuspects': '「疑似」= 范围内失败 {min} 次以上的来源（来源 IP 或被撞账号），表示值得看一眼，不等于确认的攻击。',
  'opsTipBlocked': '口径：锁定挡回（审计，持久）+ 认证限流挡回（进程内计数，重启归零）；括号里是拆分。「反复失败占比」看的是效果——失败是否集中在少数来源。',
  'opsTipTurns': '口径：AI 开口一轮算一次；来源：ai_conversation_logs，消息数取同窗口的群消息 + 私信。点卡片看对话日志。',
  'opsTipNewUsers': '口径：范围内注册成功次数；来源：system_logs 的 register。点卡片看明细。',
  'opsFootnote': '数字来自审计日志与库表计数，窗口按 UTC 算（当前：{window}）；限流挡回数是本次服务启动以来的计数，重启归零，「—」表示它只覆盖了部分窗口。「疑似」指范围内失败达到门槛的来源，不等于确认的攻击。',
  'homeAttention': '需要你处理',
  'homeAllClear': '暂无待处理，一切正常',
  'homeGo': '去处理',
  'homePending': '待处理的申请 {n} 条',
  'homePendingHint': '好友申请 / 入群申请 / 成员邀请，都等你审批',
  'homeSuspects': '疑似攻击来源 {n} 个',
  'homeSuspectsHint': '近 24 小时同一来源失败 {min} 次以上',
  'homeLockouts': '有 {n} 次失败被锁',
  'homeLockoutsHint': '打满失败阈值的来源已被暂时挡回',
  'homeNoPoolKey': 'API Key 池没有启用的 Key',
  'homeNoPoolKeyHint': 'AI 现在调不到模型，加一个再启用',
  'homeNoBackup': '还没有本地备份',
  'homeNoBackupHint': '开启每日备份，或先手动导一份',
  'homeBackupStale': '最近一次备份在 {n} 天前',
  'homeBackupStaleHint': '自动备份可能没跑起来',
  'homeMaintenanceOn': '维护模式开启中',
  'homeMaintenanceOnHint': '用户看到弹窗或提示条，API 行为按模式而定',
  'homeNumbers': '平台现状',
  'homeNumbersHint': '前三个是总数，其余是近 24 小时；点进去看明细',
  'homeRecent': '最近访问',
  'homeAllEntries': '全部入口',
  'homeAllEntriesHint': '左边栏只列当前工作区，这里按工作区一次铺开',
  'homeLast24h': '24h',
  'systemSettings': '平台全局设置',

  // v0.2.0 邮箱认证
  'auth': '认证设置',
  'authDesc': 'SMTP邮箱配置、验证码登录',
  'smtpConfig': 'SMTP 邮件配置',
  'smtpHost': 'SMTP 主机',
  'smtpPort': '端口',
  'smtpUsername': '用户名',
  'smtpPassword': '密码（授权码）',
  'smtpPasswordPlaceholder': '留空不修改已保存的密码',
  'smtpFromEmail': '发件邮箱',
  'smtpFromName': '发件人名称',
  'smtpUseTls': '使用 TLS',
  'smtpTest': '测试连接',
  'smtpTesting': '测试中...',
  'smtpTestSuccess': 'SMTP 连接成功',
  'smtpTestFailed': 'SMTP 连接失败',
  'smtpNotConfigured': '尚未配置 SMTP',
  'smtpAdd': '添加发件邮箱',
  'smtpDelete': '删除',
  'smtpActive': '启用',
  'smtpInactive': '停用',
  'smtpPriority': '优先级（越小越优先）',
  'smtpMinOne': '至少保留一个发件配置',
  'requireEmailVerification': '要求邮箱验证',
  'requireEmailVerificationDesc': '开启后注册需验证邮箱，已有用户不受影响',
  'requireEmailVerificationWarning': '请先配置并测试 SMTP 后再开启邮箱验证',
  'loginProviders': '登录方式',
  'loginProvidersDesc': '至少保留一种登录方式',
  'loginProviderDirect': '用户名+密码（直接登录）',
  'loginProviderEmail': '邮箱+验证码',
  'loginProviderWechat': '微信登录',
  'loginProviderQQ': 'QQ 登录',
  'loginProviderLastWarning': '不能禁用最后一种登录方式',

  // v0.2.0 邮件模板
  'emailTemplates': '邮件模板',
  'emailPresetGradient': '渐变版',
  'emailPresetSimple': '简版',
  'emailPresetCustom': '自定义版',
  'emailPresetGradientActive': '渐变版模板使用中',
  'emailPresetSimpleActive': '简版模板使用中',
  'emailPresetHint': '选择「自定义版」可编辑完整的邮件 HTML',
  'emailTemplateViewPreview': '查看预览',
  'emailTemplateSubject': '邮件主题',
  'emailTemplateBody': 'HTML 正文',
  'emailTemplateReset': '重置为默认',
  'emailTemplateResetAll': '全部重置',
  'emailTemplatePreview': '预览',
  'emailTemplateVariables': '可用变量',
  'emailTemplateVarHint': '模板中使用 {变量名} 格式，发送时自动替换为实际值。可用变量: {code}, {from_name}, {username}, {purpose_label}, {instance_name}, {expire_minutes}',
  'emailTemplatesSave': '保存模板',
  'emailTemplatesSaved': '模板已保存',

  // v0.2.0 LLM 厂商预设
  'llmProvider': 'LLM 厂商预设',
  'manualConfig': '手动配置',
  'defaultChatModel': '默认聊天模型',
  'defaultWorkModel': '默认工作模型',
  'modelOptionsJson': '模型选项列表 (JSON)',
  'addProvider': '添加供应商',
  'noProvidersYet': '尚未配置任何 API 供应商',
  'providerName': '供应商名称',
  'confirmDeleteProvider': '确定删除供应商「{name}」？',
  'thinkingSupported': '支持深度推理',
  'setAsDefault': '设为默认',
  'fetchModels': '获取模型',
  'fetchModelsHint': 'API Key（仅用于获取，不会保存）',
  'fetchModelsOk': '已获取 {n} 个模型',
  'fetchModelsEmpty': '该供应商没有返回模型列表，请手动填写',
  'defaultProvider': '默认厂商',
  'defaultLanguage': '全局默认语言',
  'defaultLanguageDesc': '新用户未主动选择时将沿用此默认语言。已设置过语言的用户不受影响。',
  'defaultPlatformCredit': '全局默认平台赠送额度',
  'defaultPlatformCreditDesc': '新用户注册时自动获得此额度。修改该值会批量更新所有用户的平台赠送额度（差值法）。0 = 禁用赠送。需至少有一个活跃的池 Key。',
  'platformCreditNoActiveKey': 'API Key 池中没有活跃的 Key，无法启用平台赠送额度。请先添加并激活至少一个池 Key。',
  'platformCreditConfirm': '确认修改平台赠送额度？\n\n当前 {old} → 新值 {new}\n差值 = {delta}\n\n这将影响所有 {userCount} 个用户！',
  'defaultFileQuota': '用户默认文件配额（MB）',
  'defaultFileQuotaDesc': '新用户注册时的初始文件存储配额。修改该值会批量调整所有用户的基数额度（兑换码加成不受影响）。',
  'fileQuotaIncreaseConfirm': '确认提高文件配额基数？\n\n当前 {old}MB → 新值 {new}MB\n所有用户将增加 +{delta}MB',
  'fileQuotaDecreaseConfirm': '确认降低文件配额基数？\n\n当前 {old}MB → 新值 {new}MB\n所有用户基数额度变为 {new}MB（兑换码加成保留）。\n\n⚠ 使用量超限的用户将无法上传新文件。',
  'presets': '预设',

  // ======================== 管理员 Tab ========================
  'panel': '管理面板',
  'overview': '概览',
  'users': '用户',
  'agents': 'AI 管理',
  'groups': '群聊审查',
  'codes': '兑换码',
  'opencli': 'OpenCLI',
  'logs': '对话日志',
  'federation': '联邦',
  'files': '文件',
  'usage': '用量分析',
  'apikeyPool': 'API 密钥池',
  'backup': '备份',
  'audit': '审计',
  'systemMetrics': '系统监控',
  'system': '平台设置',
  'systemDesc': '全局默认语言等',
  'systemCategory': '系统配置',

  // ======================== 管理员 - 维护模式 / 概览 ========================
  'maintenanceMode': '维护模式',
  'maintenanceStatusPaused': '暂停服务',
  'maintenanceStatusTip': '维护提示',
  'maintenanceStatusNormal': '正常',
  'maintenanceResumeService': '恢复服务',
  'maintenancePauseService': '暂停服务',
  'maintenanceCancelTip': '取消提示',
  'maintenanceShowTip': '维护提示',
  'serverStatus': '服务器状态',
  'maintenanceStarting': '启动中',
  'maintenanceRunning': '· 运行中 ·',
  'maintenanceStopped': '暂停服务',
  'maintenanceTipLabel': '· 维护提示 ·',
  'maintenanceDescAuto': '后端启动/关闭中——自动暂停服务',
  'maintenanceDescHard': '管理员暂停了服务',
  'maintenanceDescSoft': '管理员开启了维护提示',
  'maintenanceDescNormal': '服务完全正常',
  'broadcast': '全平台播报',

  // ======================== 管理员 - API Key 池 / ApiKeyPoolTab ========================
  'confirmDeletePoolKey': '确定删除池 Key「{name}」？绑定此 Key 的用户将被自动解绑。',
  'apiKeyPool': 'API Key 池',
  'apiKeyPoolDesc': '管理系统级共享 API Key，用户兑换额度后自动从池中分配',
  'editKey': '编辑',
  'testKey': '测通',
  'testing': '测试中',
  'testOk': '连接正常',
  'testFailed': '测通失败',
  'testModels': '可用模型 {n} 个',
  'editApiKeyModal': '编辑池 Key',
  'keyApiKeyKeepHint': '留空 = 不改明文',
  'addKey': '添加 Key',
  'noApiKeys': '暂无 API Key',
  'noKeysDesc': '添加系统级共享 API Key，用户兑换额度后可自动使用',
  'addFirstKey': '添加第一个 Key',
  'keyColName': '名称',
  'keyColApiUrl': 'API 地址',
  'keyColKey': 'Key',
  'keyColPriority': '优先级',
  'keyColStats': '统计',
  'keyColConcurrent': '并发',
  'keyColStatus': '状态',
  'keyColAction': '操作',
  'keyEncryptedHint': 'Key 已加密存储，仅显示密文后四位',
  'clickToDisable': '点击禁用',
  'clickToEnable': '点击启用',
  'addApiKeyModal': '添加 API Key',
  'addKeyEncryptNote': 'Key 将使用 Fernet 加密存储，添加后无法查看明文。',
  'keyNameRequired': '名称 *',
  'keyNamePlaceholder': '例：DeepSeek 主账号',
  'keyApiBaseUrl': 'API Base URL',
  'keyApiUrlPlaceholder': '留空继承全局设置',
  'keyApiKey': 'API Key *',
  'keyPriorityLabel': '优先级（越高越优先分配）',
  'keyConcurrentLimit': '并发上限',
  'keyConcurrentLimitPlaceholder': '空=按模型默认',
  'keyConcurrentDefault': '默认',
  'keyModels': '待选模型（逗号分隔）',
  'keyModelsPlaceholder': '例：deepseek-flash, deepseek-v4-pro',
  'keyModelsHint': '留空则按关联供应商的预设；点「测通」会把探测到的模型清单自动记进来',
  'keyDefaultModel': '默认模型',
  'keyDefaultModelAuto': '自动（清单第一个）',
  'keyModelsSummary': '{n} 个可选 · 默认 {model}',
  'keyModelsUnset': '未记模型（按供应商预设）',
  'testModelsSaved': '模型清单已记进这条 Key',
  'viewKeyStats': '查看使用统计',
  'keyStatsTitle': '{name} 使用统计',
  'lastNDays': '最近 {n} 天',
  'keyStatsTotalReqs': '总请求数',
  'keyStatsTotalTokens': '总 Token',
  'keyStatsTotalCredit': '总消耗额度',
  'keyStatsActiveUsers': '活跃用户',
  'keyStatsTokenTrend': 'Token 消耗趋势',
  'keyStatsModelDist': '模型分布',
  'adding': '添加中...',
  'add': '添加',

  // ======================== 管理员 - 系统监控 / SystemMetricsTab ========================
  'noMetrics': '暂无监控数据（等待首个 60 秒 flush 周期）',
  'metricsRetention': '保留 {retentionDays} 天 · 每 60s 刷新',
  'metricsLlmCalls': 'LLM 调用次数',
  'metricsAvgLatency': '平均延迟',
  'metricsErrorRate': '错误率',
  'metricsMaxQueueDepth': '队列最大深度',
  'metricsLatencyTrend': 'LLM 延迟趋势',
  'metricsAvgLatencyS': '平均延迟(s)',
  'metricsMsgPerSec': '消息/秒',
  'metricsErrorDistribution': '错误分布',
  'metricsToolStats': '工具调用统计',
  'toolColName': '工具',
  'toolColCalls': '调用',
  'toolColAvg': '平均',
  'toolColSuccessRate': '成功率',
  'willingnessDistribution': '意愿评分分布',

  // ======================== 管理员 - 对话日志 / ConversationLogTab ========================
  'convlogGlobal': '全局设置',
  'convlogPerAgent': '按 AI 设置',
  'convlogViewer': '查看日志',
  'convlogConfigTitle': '全局对话日志配置',
  'convlogHardLimit': '活跃状态保留数（3 天内动过的会话）',
  'convlogDefaultLimit': '新用户默认保留数（≤ 系统上限）',
  'convlogIdleKeep': '沉寂状态保留数（3 天以上没动）',
  'convlogAgedKeep': '老旧状态保留数（30 天以上没动）',
  'convlogIdleDays': '沉寂阈值（天）',
  'convlogAgedDays': '老旧阈值（天）',
  'convlogKnobDefault': '留空 = 用默认',
  'convlogDefaultAccess': '默认允许用户查看 AI 对话日志',
  'convlogDefaultDelay': '新 AI 默认开启延迟回复',
  'convlogDefaultDelayDesc': '关闭时新创建的 AI 需手动开启延迟回复 Skill',
  'convlogCompressThreshold': '上下文压缩阈值',
  'convlogCompressThresholdDesc': '达到上下文窗口的此百分比时自动压缩。调低=更早压缩，调高=收集更多消息再压缩',
  'convlogIdleThreshold': '久未活跃压缩系数',
  'convlogIdleThresholdDesc': '冷触发线在「压后上限」到「压缩阈值」之间的位置（默认 1/e ≈ 37%）。越大越接近压缩阈值，越小越接近压后上限',
  'convlogTargetPercent': '压缩后目标上限',
  'convlogTargetPercentDesc': '压缩后摘要占压缩阈值的比例（默认 20%），也是久未活跃阈值的下界',
  'saveConfig': '保存配置',
  'perAgentSettings': '按 AI 单独设置',
  'selectAi': '选择 AI',
  'selectAiPlaceholder': '-- 选择 AI --',
  'currentEffective': '当前生效：保留 <b>{retention}</b> 条 · 用户访问 <b>{access}</b>',
  'retentionLimit': '保留上限（留空=系统上限 {max}）',
  'allowUserViewLogs': '允许用户查看日志',
  'defaultOn': '全局默认开',
  'defaultOff': '全局默认关',
  'saveSettings': '保存设置',
  'groupChat': '群聊',
  'directMessage': '私信',
  'hasOutput': '有产出',
  'deepReasoning': '深度推理',
  'messages': '条消息',
  'tokens': 'tokens:',
  'conversationLog': '对话日志 #{id}',
  'logType': '类型:',
  'logMessageCount': '消息数:',
  'logModel': '模型:',

  // ======================== 管理员 - 联邦 / FederationTab ========================
  'confirmRemovePeer': '确定移除此对等端？相关群聊共享也将被删除。',
  'urlRequired': 'URL 不能为空',
  'rotateFailed': '轮换失败',
  'savingToken': '正在保存 Token...',
  'tokenSaveFailed': 'Token 保存失败',
  'verifyingRegister': '正在验证公网 URL 可达性与身份匹配...',
  'registerFailed': '注册失败',
  'saveOrRegisterFailed': '保存/注册失败',
  'tokenUpdated': 'GitHub Token 已更新',
  'confirmRegenerateId': '确认重新生成公网 ID？旧 ID 将作废。如果已注册到 GitHub，需要重新注册。',
  'pleaseEnterToken': '请先填入 GitHub Token',
  'tokenSavedEncrypted': 'GitHub Token 已加密保存',
  'connected': '已连接',
  'connecting': '连接中',
  'failed': '失败',
  'connectFailed': '连接失败',
  'addPeerFailed': '添加对等端失败',
  'deletePeerFailed': '删除对等端失败',
  'disconnectFailed': '断开连接失败',
  'rotating': '轮换中',
  'registerNotice': '注册公网 ID 须知',
  'tokenConfiguredClickChange': '已配置 Token（点击更换）',
  'pasteNewToken': '粘贴新 Token 替换旧 Token',
  'tokenHintOrGet': '留空则使用已存储的 Token。获取:',
  'githubToken': 'GitHub Token',
  'tokenHelpText': '需要 Token 才能写入注册表。点击链接 → Generate new token (classic) → 勾选 repo',
  'saveTokenAndRegister': '保存 Token 并注册',
  'knownAndContinue': '我已知晓，继续注册',
  'instanceIdentity': '实例身份',
  'displayName': '显示名称',
  'instanceNamePlaceholder': '我的实例',
  'publicUrlLabel': '公网地址（输入域名或 IP，自动拼接 /federation/ws）',
  'publicIdLabel': '公网 ID（启动时自动生成 ULID，唯一性 ≈ 1/43亿）',
  'regenerate': '重新生成',
  'githubTokenLabel': 'GitHub Token （获取 →）',
  'tokenConfiguredPlaceholder': '已配置（重新输入会覆盖）',
  'tokenPlaceholder': 'ghp_xxxxxxxxxxxxxxxx',
  'saveToken': '保存 Token',
  'tokenConfigured': '已配置 Token（加密存储）',
  'saveInstanceInfo': '保存实例信息',
  'registering': '注册中...',
  'registered': '✓ 已注册',
  'registerToGithub': '注册到 GitHub',
  'saveAndRetry': '保存并重试',
  'instanceId': '子网 ID',
  'publicId': '公网 ID（ULID 自动生成）',
  'notGenerated': '未生成',
  'publicUrl': '公网 URL',
  'notConfigured': '未配置',
  'change': '更换',
  'configure': '配置',
  'tokenConfiguredEncrypted': '已配置（加密存储）',
  'peers': '对等端 ({count})',
  'addPeer': '添加',
  'peerPublicId': '公网 ID',
  'peerIdPlaceholder': 'Copree-xxxxxxxx',
  'peerDisplayName': '显示名称',
  'peerNamePlaceholder': '花花的实例',
  'peerWsUrl': 'WebSocket 地址（输入 IP:端口 或域名，自动拼接 /federation/ws）',
  'optional': '选填',
  'peerUrlOptionalHint': '留空则自动从 GitHub 注册表获取。对方未注册也可以留空——只要对方能主动连过来（单向连接即可双向通信）。',
  'sharedSecret': '共享密钥',
  'secretPlaceholder': '至少 8 位字符',
  'addAndConnect': '添加并连接',
  'noPeers': '暂无对等端，点击"添加"连接其他 Copree 实例',
  'rotateUrl': '轮换 URL',
  'disconnect': '断开',
  'connect': '连接',
  'peerUrl': '公网 URL',
  'secretNoChange': '共享密钥（留空则不修改）',
  'newSecretPlaceholder': '输入新密钥替换旧密钥',
  'rotateHint': 'URL 轮换 — 新 URL 测试成功后旧 URL 才会失效',
  'initiateRotate': '发起轮换',
  'urlRotated': 'URL 已轮换',
  'lastRotated': '最近:',
  'federationGroups': '联邦群聊',
  'federationGroupsDesc': '在群聊设置面板中为各群聊配置联邦共享。联邦群聊中的消息将自动转发到所选的对等端。',
  'federationGroupsHint': '提示：请先在"对等端"中添加并连接其他实例，然后在群聊设置中开启联邦共享。',
  'federationEntities': '联邦实体 ({count})',
  'federationEntitiesHint': '已接入的联邦实体列表。通过 entity_announce 协议自动注册，或由远端管理员共享。',
  'noFederatedEntities': '暂无联邦实体。当远端实例共享群聊/DM 后，会自动出现在此列表中。',
  'confirmRemoveEntity': '确定移除此联邦实体？相关消息转发将停止。',
  'federationDiscovery': '公开发现（可选）',
  'federationDiscoveryDesc': 'GitHub 注册表只是一个公开"电话本"，让其他实例能发现你。不注册也能联邦通信——只要双方知道彼此的 URL 和共享密钥直连即可。',
  'federationDiscoveryHint': '如果你希望自己的实例被更多人在 GitHub 注册表中搜索到，可以配置 Token 并点击上方「注册到 GitHub 公开目录」。',
  'registerNoticeText': '将公网 ID 注册到 GitHub 注册表后：',
  'registerNoticeItem1': '你的公网 ID、显示名称、公网 URL 将公开可见',
  'registerNoticeItem2': '其他 Copree 实例可以通过注册表发现并尝试连接你的实例',
  'registerNoticeItem3': '连接请求需要双方配置共享密钥才能成功握手',
  'registerNoticeItem4': '你可以随时在注册表中更新或删除自己的条目',
  'registerNoticeItem5': '请确保公网 URL 指向的是你自己的实例，不要冒用他人地址',
  'registerNoticeVerify': '注册前系统会验证你的公网 URL 确实指向运行中的 Copree 实例。',
  'enablePeer': '启用',
  'disablePeer': '禁用',

  // ======================== 管理员 - OpenCLI ========================
  'globalConfig': '全局设置',
  'enableOpenCLI': '启用 OpenCLI',
  'rateLimit': '默认速率限制（次/分钟）',
  'timeoutSeconds': '超时时间（秒）',
  'opencliAiWhitelist': 'AI OpenCLI 白名单',
  'opencliAiWhitelistColId': 'ID',
  'opencliAiWhitelistColName': '名称',
  'opencliAiWhitelistColOwner': '所属用户',
  'opencliAiWhitelistColEnabled': 'OpenCLI',
  'opencliAiWhitelistColRate': '速率限制',
  'opencliAiWhitelistColAction': '操作',
  'presetCommands': '预设命令（新手一键添加）',
  'presetCommandsDesc': '以下是AI最常用的命令，点击「添加」即可加入命令白名单。已添加的命令不会重复。',
  'addAllPresets': '一键添加全部预设',
  'allAdded': '✓ 已全部添加',
  'manualAddCommand': '手动添加命令',
  'manualAddDesc': '如需添加不在预设中的命令（例如接入其他网站），可在此手动输入。精确模式：匹配确切命令名。正则模式：匹配命令规则。',
  'commandPattern': '命令/正则',
  'commandPatternPlaceholder': 'bilibili 或 gh .*',
  'regexMode': '正则',
  'description': '描述',
  'addCmd': '添加',
  'commandWhitelist': '命令白名单列表',
  'cmdColPattern': '模式',
  'cmdColType': '类型',
  'cmdColDesc': '描述',
  'cmdColStatus': '状态',
  'cmdColAction': '操作',
  'regex': '正则',
  'exact': '精确',
  'noCommands': '暂无命令白名单 — 点击上方预设卡片快速添加',
  'usageLogs': '使用日志',
  'logsColAi': 'AI',
  'logsColCmd': '命令',
  'logsColExitCode': '退出码',
  'logsColDuration': '耗时',
  'logsColOutput': '输出预览',

  // ======================== 管理员 - 用量分析 / UsageDashboardTab ========================
  'totalTokens': '总 Token',
  'totalCalls': '总调用次数',
  'activeAgents': '活跃 AI',
  'activeUsers': '活跃用户',
  'dailyTokenConsumption': '全站每日 Token 消耗',
  'usageByUser': '按用户 · AI 用量明细',
  'aiCount': '个 AI',
  'callsCount': '次调用',
  'noData': '暂无数据',
  'aiDailyTokens': 'AI #{id} 每日 Token 分布',
  'thinking': '思考',
  'cached': '缓存',
  'cacheHitRate': '缓存命中率',
  'noAiDailyData': '该 AI 暂无每日数据',
  'daysSuffix': '天',
  'usageTabResidents': '居民 AI',
  'usageTabWorlds': '群视界',
  'residentsDailyTokens': '居民 AI 每日 Token 消耗',
  'worldsUsage': '群视界用量',
  'worldsUsageEmpty': '所选区间内没有群视界调用',
  'worldsDailyTokens': '群视界每日 Token 消耗',
  'worldsActive': '有调用的世界',
  'worldColWorld': '世界',
  'worldColOwner': '主人',
  'worldColLastAt': '最近调用',

  // ======================== 管理员 - 能力面板 / CapabilitiesTab ========================
  'toolRegistry': '工具注册表',
  'skillManagement': 'AI 技能管理',
  'toolsColName': '工具名',
  'toolsColDesc': '描述',
  'toolsColSegment': '所属段',
  'toolsColStates': '支持状态',
  'toolsFilterSegment': '按段筛选',
  'toolsFilterState': '按状态筛选',
  'toolsFilterAll': '全部',
  'toolsTotal': '{n} 个工具',
  'toolsSegments': '{n} 个技能段',
  'toolsParamSchema': '参数 Schema',
  'skillsSelectAgent': '选择 AI',
  'skillsColName': '技能名',
  'skillsColType': '类型',
  'skillsColEnabled': '启用',
  'skillsColConfig': '配置',
  'skillsColAction': '操作',
  'skillsToggle': '切换',
  'skillsNoAgent': '请先选择一个 AI',
  'skillsLoadFailed': '加载技能失败',
  'skillsToggleFailed': '切换失败',
  'skillBackpack': '技能背包',
  'registrationEnabled': '开放注册',
  'registrationEnabledDesc': '关闭后新用户无法通过注册页面创建账号，管理员仍可手动创建',
  'createUser': '创建用户',
  'createUserTitle': '手动创建用户',
  'createUserUsername': '用户名',
  'createUserPassword': '密码',
  'createUserEmail': '邮箱（可选）',
  'importCsv': '导入 CSV',
  'importCsvTitle': 'CSV 批量导入用户',
  'importCsvHint': 'CSV 格式：username,password[,email]。第一行可以是表头。',
  'createUserUsernameHint': '至少 2 个字符',
  'createUserPasswordHint': '至少 6 位',
  'importCsvRowResult': '第 {row} 行',

  // ---- 文件清理 ----
  'cleanup': '文件清理',
  'cleanupDesc': '扫描并清理无引用头像文件和失效文件映射',
  'cleanupRun': '运行清理',
  'cleanupRunning': '清理中...',
  'cleanupLastRun': '上次清理',
  'cleanupFilesDeleted': '清理文件',
  'cleanupOrphansDeleted': '失效映射',
  'cleanupNever': '从未运行',
}

export const adminEn: TranslationDict = {
  'title': 'Admin Panel',
  'subtitle': 'System Management & Monitoring',
  'backToList': 'Back to List',
  'backToApp': 'Back to App',
  'collapseRail': 'Collapse navigation',
  'expandRail': 'Expand navigation',
  'backToMe': 'Back to Me',
  'groupGeneral': 'General',
  'groupPeople': 'People & Content',
  'groupCapabilities': 'Capabilities & Integrations',
  'groupMonitoring': 'Monitoring & Logs',
  'groupData': 'Data & Maintenance',
  'capabilities': 'Plugins & Skills',
  'capabilitiesPlugins': 'Plugins',
  'capabilitiesDesc': 'Plugins, tools, skills and skill backpack',
  'prompt': 'System Prompts',
  'promptDesc': 'Inspect and customize the system prompt segments sent to AI',
  'store': 'Store',
  'storeDesc': 'Plugin market, installed plugins and market sync source (the world market stays public)',
  'overviewDesc': 'Site scale and maintenance mode',
  'usersDesc': 'Accounts, roles and admin rights',
  'agentsDesc': 'All AI agents and their self-edit switch',
  'groupsDesc': 'Group chat audit and dismissal',
  'codesDesc': 'Generate and review redeem codes',
  'apikeyPoolDesc': 'Shared API key pool and call stats',
  'opencliDesc': 'CLI bridge configuration, authorization and logs',
  'federationDesc': 'Interop and federation with other sites',
  'metricsDesc': 'Real-time CPU, memory and disk metrics',
  'usageDesc': 'Model call and credit usage analytics',
  'convlogDesc': 'Search message history across all conversations',
  'auditDesc': 'Admin operation audit trail',
  'backupDesc': 'Export and restore database and files',
  'totalUsers': 'Total Users',
  'totalAgents': 'Total AI',
  'totalGroups': 'Total Groups',
  'total': 'total',
  'pendingRequests': 'Pending Requests',
  'usersColId': 'ID',
  'usersColUsername': 'Username',
  'usersColRole': 'Role',
  'usersColQuota': 'Quota',
  'usersColStatus': 'Status',
  'usersColAction': 'Actions',
  'active': 'Active',
  'banned': 'Banned',
  'ban': 'Ban',
  'unban': 'Unban',
  'confirmPromote': 'Confirm promote {username} to admin?',
  'promote': 'Promote',
  'confirmDemote': "Confirm revoke {username}'s admin privileges?",
  'demote': 'Demote',
  'resetPassword': 'Reset Password',
  'resetPasswordTitle': 'Reset Password for {username}',
  'resetPasswordPlaceholder': 'Enter new password (min 6 chars)',
  'resetPasswordSuccess': 'Password reset successfully',
  'resetPasswordError': 'Failed to reset password',
  'agentsColId': 'ID',
  'agentsColName': 'Name',
  'agentsColOwner': 'Owner',
  'agentsColStatus': 'Status',
  'agentsColSelfEdit': 'Self-Modify',
  'agentsColAction': 'Actions',
  'searchAiPlaceholder': 'Search AI by name...',
  'disableSelfEdit': 'Disable Self-Modify',
  'enableSelfEdit': 'Enable Self-Modify',
  'groupsColId': 'ID',
  'groupsColName': 'Name',
  'groupsColOwner': 'Owner',
  'groupsColVector': 'Vector Accel',
  'groupsColAction': 'Actions',
  'enabled': 'Enabled',
  'notEnabled': 'Not Enabled',
  'disabled': 'Disabled',
  'confirmDismissGroup': 'Confirm dismiss this group?',
  'dismiss': 'Dismiss',
  'creditApi': 'API Credit',
  'creditBundle': 'AI Bundle',
  'creditFile': 'File Quota',
  'generateFailed': 'Generation failed, check parameters',
  'generateCode': 'Generate Code',
  'codeType': 'Type',
  'expiresDays': 'Validity (days)',
  'isApiPool': 'API Pool Quota',
  'generating': 'Generating...',
  'generate': 'Generate',
  'codeNote': 'Note (admin visible)',
  'codeNotePlaceholder': 'e.g., API quota for John',
  'maxUsageLabel': 'Max usage per code (empty = one-time full)',
  'balanceHint': '1 credit = 10,000 tokens',
  'codeOneTimeWarning': 'Copy and save this code, it is shown only once',
  'codeList': 'Code List',
  'codesColCode': 'Code',
  'codesColType': 'Type',
  'codesColAmount': 'Amount',
  'codesColNote': 'Note',
  'codesColExpires': 'Expires',
  'codesColStatus': 'Status',
  'pool': 'Pool',
  'codeTypeDefault': 'AI Creation Quota',
  'usedBy': 'Used',
  'available': 'Available',
  'enable': 'Enable',
  'disable': 'Disable',
  'delete': 'Delete',
  'save': 'Save',
  'cancel': 'Cancel',
  'saving': 'Saving...',
  'saveSuccess': 'Saved successfully',
  'saveFailed': 'Save failed',
  // Notice lines in the system-settings / system-prompt tabs (were hardcoded + tone guessed from Chinese text)
  'updateFailed': 'Update failed',
  'uploadLimitUpdated': 'Upload size limit updated',
  'avatarLimitUpdated': 'Avatar size limit updated',
  'bulkConcurrencyConfirm': 'Set the AI concurrency limit of every group to {n}? This cannot be undone.',
  'bulkConcurrencySet': 'Concurrency limit of every group set to {n}',
  'bulkUpdateFailed': 'Bulk update failed',
  'bulkApplyRunning': 'Working…',
  'bulkApply': 'Apply to all',
  'promptResetDone': 'Restored to default',
  'promptResetFailed': 'Reset failed',
  'promptOrderSaved': 'Order saved',
  'promptOrderSaveFailed': 'Failed to save order',
  'allUsers': 'all users',
  'operationFailed': 'Operation failed',
  'deleteFailed': 'Delete failed',
  'loadFailed': 'Load failed',
  'downloadFailed': 'Download failed',
  'dbBackupSuccess': '✓ Database backup downloaded',
  'fullBackupSuccess': '✓ Full backup downloaded (database + all files)',
  'restoreFailed': 'Restore failed',
  'dbRestoreSuccess': '✓ Database restored, please refresh',
  'dbRestoreRestart': '✓ Database restored, please restart the application',
  'fullRestoreSuccess': '✓ Full backup restored: database + all files, please refresh',
  'fullRestoreRestart': '✓ Full backup restored: database + all files, please restart the application',
  'presetsAddComplete': 'Preset commands added',
  'presetsAddFailed': 'Failed to add presets',
  'exportTitle': 'Export Data',
  'exportDesc': 'Two backup formats, choose as needed.',
  'fullBackup': 'Full Backup (Recommended)',
  'fullBackupDesc': 'Exports .tar.gz containing: all database data + all files under /app/data/ (AI files, attachments, avatars, etc). Can be used for full migration.',
  'packing': 'Packing...',
  'downloadFullBackup': 'Download Full Backup',
  'dbOnly': 'Database Only',
  'dbOnlyDesc': 'Exports .sql file, data structure and content only. Smaller but does NOT contain uploaded attachments and AI files.',
  'exporting': 'Exporting...',
  'downloadDbOnly': 'Download Database Only',
  'restoreTitle': 'Restore Data',
  'restoreWarning': 'Restore will OVERWRITE all current data. Please confirm the backup file is correct.',
  'currentDbBackend': 'Current Database',
  'backupFileExt': 'Backup file extension',
  'fullRestore': 'Full Restore',
  'fullRestoreDesc': 'Upload .tar.gz full backup to restore database + all files.',
  'restoringFull': 'Full restore in progress...',
  'dbRestore': 'Database Restore Only',
  'dbRestoreDesc': 'Upload .sql file to restore database only.',
  'restoring': 'Restoring...',
  'logsColTime': 'Time',
  'logsColType': 'Type',
  'logsColOperator': 'Operator',
  'logsColTarget': 'Target',
  'logsColDetail': 'Details',
  'logsColResult': 'Result',
  'logsResultOk': 'OK',
  'logsResultFailed': 'Failed',
  'logsAnonymous': 'Anonymous',
  'logsFilterType': 'Type',
  'logsFilterAll': 'All',
  'opsTab': 'Ops Overview',
  'opsTabDesc': 'One screen: logins, blocks, conversations, sign-ups',
  'opsRangeLabel': 'Range',
  'opsRange1': 'Last 24 hours',
  'opsRange7': 'Last 7 days',
  'opsRange30': 'Last 30 days',
  'opsLoginOk': 'Successful logins',
  'opsLoginFail': 'Failed logins',
  'opsSuspects': 'Suspected attackers',
  'opsBlocked': 'Blocked',
  'opsTurns': 'AI turns',
  'opsNewUsers': 'New users',
  'opsLoginOkValue': '{n}',
  'opsLoginFailValue': '{n}',
  'opsSuspectsValue': '{n}',
  'opsBlockedValue': '{n}',
  'opsTurnsValue': '{n} turns',
  'opsNewUsersValue': '{n}',
  'opsSubAccounts': '{n} accounts',
  'opsSubTop': 'busiest source: {n} failures',
  'opsNoSuspects': 'no suspicious source',
  'opsSubFailLocked': '{n} of them hit the lockout threshold',
  'opsSubRepeat': '{p}% of failures come from repeat sources (2+)',
  'opsSubBlocked': '{a} by lockout · {b} by rate limit',
  'opsSubBlockedPartial': '{a} by lockout · rate limit — (only {b} since start)',
  'opsSubMessages': '{n} messages',
  'opsSubTotalUsers': '{n} accounts in total',
  'opsTipLoginOk': 'Definition: successful logins in range; source: system_logs (login). Click the card for details.',
  'opsTipLoginFail': 'Definition: real wrong-password attempts (lockout rejections live under "Blocked"); source: system_logs (login_failed). Click for details.',
  'opsTipSuspects': '"Suspected" = a source IP or targeted account with {min}+ failures in range — worth a look, not a confirmed attack.',
  'opsTipBlocked': 'Definition: rejections by lockout (audit, persistent) + auth rate limit (in-process, resets on restart). The repeat-share line shows effectiveness: are failures concentrated in a few sources?',
  'opsTipTurns': 'Definition: one AI turn each; source: ai_conversation_logs, messages from group + DM in the same range. Click for the conversation log.',
  'opsTipNewUsers': 'Definition: successful sign-ups in range; source: system_logs (register). Click for details.',
  'opsFootnote': 'Numbers come from the audit log and table counts; the window is UTC ({window}). The rate-limit figure counts since this service start and resets on restart; "—" means it covers only part of the window. "Suspected" means a source hit the failure threshold — not a confirmed attack.',
  'homeAttention': 'Needs your attention',
  'homeAllClear': 'Nothing pending — all clear',
  'homeGo': 'Open',
  'homePending': '{n} requests awaiting approval',
  'homePendingHint': 'Friend, group-join and member-invite requests',
  'homeSuspects': '{n} suspected attack sources',
  'homeSuspectsHint': 'Same source failed {min}+ times in the last 24h',
  'homeLockouts': '{n} lockouts after repeated failures',
  'homeLockoutsHint': 'Sources that hit the failure threshold are blocked for a while',
  'homeNoPoolKey': 'No active key in the API key pool',
  'homeNoPoolKeyHint': 'AI cannot reach any model — add a key and enable it',
  'homeNoBackup': 'No local backup yet',
  'homeNoBackupHint': 'Turn on daily backup, or export one now',
  'homeBackupStale': 'Last backup was {n} days ago',
  'homeBackupStaleHint': 'The daily backup job may not be running',
  'homeMaintenanceOn': 'Maintenance mode is on',
  'homeMaintenanceOnHint': 'Users see a dialog or banner; API behavior depends on the mode',
  'homeNumbers': 'Where things stand',
  'homeNumbersHint': 'First three are totals, the rest the last 24 hours — click for details',
  'homeRecent': 'Recently visited',
  'homeAllEntries': 'All entries',
  'homeAllEntriesHint': 'The left rail lists only the current workspace; here is everything',
  'homeLast24h': 'last 24h',
  'systemSettings': 'Platform Global Settings',
  'auth': 'Auth Settings',
  'authDesc': 'SMTP config, verification code login',
  'smtpConfig': 'SMTP Configuration',
  'smtpHost': 'SMTP Host',
  'smtpPort': 'Port',
  'smtpUsername': 'Username',
  'smtpPassword': 'Password (Auth Code)',
  'smtpPasswordPlaceholder': 'Leave blank to keep current',
  'smtpFromEmail': 'From Email',
  'smtpFromName': 'From Name',
  'smtpUseTls': 'Use TLS',
  'smtpTest': 'Test Connection',
  'smtpTesting': 'Testing...',
  'smtpTestSuccess': 'SMTP connection successful',
  'smtpTestFailed': 'SMTP connection failed',
  'smtpNotConfigured': 'SMTP not configured',
  'smtpAdd': 'Add Sender',
  'smtpDelete': 'Delete',
  'smtpActive': 'Active',
  'smtpInactive': 'Inactive',
  'smtpPriority': 'Priority (lower = first)',
  'smtpMinOne': 'At least one SMTP config required',
  'requireEmailVerification': 'Require Email Verification',
  'requireEmailVerificationDesc': 'When on, new registrations require email verification. Existing users unaffected.',
  'requireEmailVerificationWarning': 'Please configure and test SMTP before enabling email verification',
  'loginProviders': 'Login Methods',
  'loginProvidersDesc': 'At least one method must remain',
  'loginProviderDirect': 'Username+Password (Direct)',
  'loginProviderEmail': 'Email+Code',
  'loginProviderWechat': 'WeChat Login',
  'loginProviderQQ': 'QQ Login',
  'loginProviderLastWarning': 'Cannot disable the last remaining login method',
  'emailTemplates': 'Email Templates',
  'emailPresetGradient': 'Gradient',
  'emailPresetSimple': 'Simple',
  'emailPresetCustom': 'Custom',
  'emailPresetGradientActive': 'Gradient template active',
  'emailPresetSimpleActive': 'Simple template active',
  'emailPresetHint': 'Select "Custom" to edit the full email HTML',
  'emailTemplateViewPreview': 'View Preview',
  'emailTemplateSubject': 'Subject',
  'emailTemplateBody': 'HTML Body',
  'emailTemplateReset': 'Reset to Default',
  'emailTemplateResetAll': 'Reset All',
  'emailTemplatePreview': 'Preview',
  'emailTemplateVariables': 'Available Variables',
  'emailTemplateVarHint': 'Use {variable} format. Available: {code}, {from_name}, {username}, {purpose_label}, {instance_name}, {expire_minutes}',
  'emailTemplatesSave': 'Save Templates',
  'emailTemplatesSaved': 'Templates saved',
  'llmProvider': 'LLM Provider Presets',
  'manualConfig': 'Manual Config',
  'defaultChatModel': 'Default Chat Model',
  'defaultWorkModel': 'Default Work Model',
  'modelOptionsJson': 'Model Options (JSON)',
  'addProvider': 'Add Provider',
  'noProvidersYet': 'No API providers configured yet',
  'providerName': 'Provider Name',
  'confirmDeleteProvider': 'Delete provider "{name}"?',
  'thinkingSupported': 'Supports deep reasoning',
  'setAsDefault': 'Set as default',
  'fetchModels': 'Fetch models',
  'fetchModelsHint': 'API key (one-off, not saved)',
  'fetchModelsOk': 'Fetched {n} models',
  'fetchModelsEmpty': 'This provider returned no model list — please fill it in manually',
  'defaultProvider': 'Default Provider',
  'defaultLanguage': 'Global Default Language',
  'defaultLanguageDesc': "New users will use this default if they haven't chosen. Users who have set their language won't be affected.",
  'defaultPlatformCredit': 'Global Default Platform Credit',
  'defaultPlatformCreditDesc': 'New users receive this credit on registration. Changing this value batch-updates all users via delta. 0 = disabled. Requires at least one active pool Key.',
  'platformCreditNoActiveKey': 'No active pool Key. Please add and activate at least one Key first.',
  'platformCreditConfirm': 'Confirm platform credit change?\n\nCurrent {old} -> New {new}\nDelta = {delta}\n\nThis affects all {userCount} users!',
  'defaultFileQuota': 'Default User File Quota (MB)',
  'defaultFileQuotaDesc': 'Initial storage quota for new users. Changing this batch-adjusts all users\' base quota (redemption bonuses are preserved).',
  'fileQuotaIncreaseConfirm': 'Confirm file quota base increase?\n\nCurrent {old}MB -> New {new}MB\nAll users will gain +{delta}MB',
  'fileQuotaDecreaseConfirm': 'Confirm file quota base decrease?\n\nCurrent {old}MB -> New {new}MB\nAll users\' base will become {new}MB (redemption bonuses kept).\n\n⚠ Users over the limit cannot upload new files.',
  'presets': 'Presets',
  'overview': 'Overview',
  'users': 'Users',
  'agents': 'AI Mgmt',
  'groups': 'Group Audit',
  'codes': 'Codes',
  'opencli': 'OpenCLI',
  'logs': 'Conv Logs',
  'federation': 'Federation',
  'files': 'Files',
  'usage': 'Usage',
  'apikeyPool': 'API Key Pool',
  'backup': 'Backup',
  'audit': 'Audit',
  'systemMetrics': 'Metrics',
  'system': 'Platform',
  'systemDesc': 'Global default language, etc.',
  'systemCategory': 'System Config',
  'maintenanceMode': 'Maintenance Mode',
  'maintenanceStatusPaused': 'Service Paused',
  'maintenanceStatusTip': 'Maintenance Notice',
  'maintenanceStatusNormal': 'Normal',
  'maintenanceResumeService': 'Resume Service',
  'maintenancePauseService': 'Pause Service',
  'maintenanceCancelTip': 'Cancel Notice',
  'maintenanceShowTip': 'Show Notice',
  'serverStatus': 'Server Status',
  'maintenanceStarting': 'Starting',
  'maintenanceRunning': '· Running ·',
  'maintenanceStopped': 'Service Paused',
  'maintenanceTipLabel': '· Notice ·',
  'maintenanceDescAuto': 'Backend starting/stopping — auto paused',
  'maintenanceDescHard': 'Admin paused the service',
  'maintenanceDescSoft': 'Admin enabled maintenance notice',
  'maintenanceDescNormal': 'Service running normally',
  'broadcast': 'Broadcast',
  'confirmDeletePoolKey': 'Delete pool key "{name}"? Users bound to this key are unlinked automatically.',
  'apiKeyPool': 'API Key Pool',
  'apiKeyPoolDesc': 'Manage system-wide shared API keys; users are assigned one from the pool automatically after redeeming credit',
  'editKey': 'Edit',
  'testKey': 'Test',
  'testing': 'Testing',
  'testOk': 'Connection OK',
  'testFailed': 'Test failed',
  'testModels': '{n} models available',
  'editApiKeyModal': 'Edit pool key',
  'keyApiKeyKeepHint': 'leave empty to keep the current key',
  'addKey': 'Add Key',
  'noApiKeys': 'No API keys yet',
  'noKeysDesc': 'Add a system-wide shared API key so users can use it automatically after redeeming credit',
  'addFirstKey': 'Add First Key',
  'keyColName': 'Name',
  'keyColApiUrl': 'API URL',
  'keyColKey': 'Key',
  'keyColPriority': 'Priority',
  'keyColStats': 'Stats',
  'keyColConcurrent': 'Concurrency',
  'keyColStatus': 'Status',
  'keyColAction': 'Actions',
  'keyEncryptedHint': 'The key is stored encrypted; only the last four characters are shown',
  'clickToDisable': 'Click to disable',
  'clickToEnable': 'Click to enable',
  'addApiKeyModal': 'Add API Key',
  'addKeyEncryptNote': 'The key is stored with Fernet encryption; the plaintext cannot be viewed afterwards.',
  'keyNameRequired': 'Name *',
  'keyNamePlaceholder': 'e.g. DeepSeek main account',
  'keyApiBaseUrl': 'API Base URL',
  'keyApiUrlPlaceholder': 'Leave blank to inherit the global setting',
  'keyApiKey': 'API Key *',
  'keyPriorityLabel': 'Priority (higher is assigned first)',
  'keyConcurrentLimit': 'Concurrency Limit',
  'keyConcurrentLimitPlaceholder': 'Blank = model default',
  'keyConcurrentDefault': 'Default',
  'keyModels': 'Models (comma separated)',
  'keyModelsPlaceholder': 'e.g. deepseek-flash, deepseek-v4-pro',
  'keyModelsHint': 'Blank = use the linked provider preset. "Test" records the models the provider reports.',
  'keyDefaultModel': 'Default model',
  'keyDefaultModelAuto': 'Auto (first in list)',
  'keyModelsSummary': '{n} available · default {model}',
  'keyModelsUnset': 'No models recorded (provider preset)',
  'testModelsSaved': 'Model list saved to this key',
  'viewKeyStats': 'View usage stats',
  'keyStatsTitle': '{name} usage stats',
  'lastNDays': 'Last {n} days',
  'keyStatsTotalReqs': 'Total Requests',
  'keyStatsTotalTokens': 'Total Tokens',
  'keyStatsTotalCredit': 'Total Credit Used',
  'keyStatsActiveUsers': 'Active Users',
  'keyStatsTokenTrend': 'Token Usage Trend',
  'keyStatsModelDist': 'Model Distribution',
  'adding': 'Adding...',
  'add': 'Add',
  'noMetrics': 'No metrics data yet (waiting for first 60s flush cycle)',
  'metricsRetention': 'Retained {retentionDays} days · Refreshes every 60s',
  'metricsLlmCalls': 'LLM Calls',
  'metricsAvgLatency': 'Avg Latency',
  'metricsErrorRate': 'Error Rate',
  'metricsMaxQueueDepth': 'Max Queue Depth',
  'metricsLatencyTrend': 'LLM Latency Trend',
  'metricsAvgLatencyS': 'Avg Latency (s)',
  'metricsMsgPerSec': 'Messages/s',
  'metricsErrorDistribution': 'Error Distribution',
  'metricsToolStats': 'Tool Call Stats',
  'toolColName': 'Tool',
  'toolColCalls': 'Calls',
  'toolColAvg': 'Avg',
  'toolColSuccessRate': 'Success Rate',
  'willingnessDistribution': 'Willingness Score Distribution',
  'convlogGlobal': 'Global Config',
  'convlogPerAgent': 'Per-AI Settings',
  'convlogViewer': 'View Logs',
  'convlogConfigTitle': 'Global Conversation Log Config',
  'convlogHardLimit': 'Retained logs for active states (touched within 3 days)',
  'convlogDefaultLimit': 'Default retention for new users (≤ system limit)',
  'convlogIdleKeep': 'Retained logs for idle states (untouched over 3 days)',
  'convlogAgedKeep': 'Retained logs for aged states (untouched over 30 days)',
  'convlogIdleDays': 'Idle after (days)',
  'convlogAgedDays': 'Aged after (days)',
  'convlogKnobDefault': 'Empty = built-in default',
  'convlogDefaultAccess': 'Default allow users to view AI conversation logs',
  'convlogDefaultDelay': 'New AI default delay reply enabled',
  'convlogDefaultDelayDesc': 'When off, newly created AI must manually enable delay reply Skill',
  'convlogCompressThreshold': 'Context compression threshold',
  'convlogCompressThresholdDesc': 'Compress once the context reaches this share of the window. Lower = compress earlier, higher = collect more messages first',
  'convlogIdleThreshold': 'Idle compaction coefficient',
  'convlogIdleThresholdDesc': 'Where the idle trigger sits between the post-compaction ceiling and the compression threshold (default 1/e ≈ 37%). Higher = closer to the threshold, lower = closer to the ceiling',
  'convlogTargetPercent': 'Post-compaction ceiling',
  'convlogTargetPercentDesc': 'Summary size as a share of the compression threshold (default 20%); also the lower bound of the idle trigger',
  'saveConfig': 'Save Config',
  'perAgentSettings': 'Per-AI Settings',
  'selectAi': 'Select AI',
  'selectAiPlaceholder': '-- Select AI --',
  'currentEffective': 'Currently active: retain <b>{retention}</b> logs · User access <b>{access}</b>',
  'retentionLimit': 'Retention limit (empty = system max {max})',
  'allowUserViewLogs': 'Allow user to view logs',
  'defaultOn': 'Default ON',
  'defaultOff': 'Default OFF',
  'saveSettings': 'Save Settings',
  'groupChat': 'Group',
  'directMessage': 'DM',
  'hasOutput': 'Has Output',
  'deepReasoning': 'Deep Reasoning',
  'messages': ' messages',
  'tokens': 'tokens:',
  'conversationLog': 'Conversation Log #{id}',
  'logType': 'Type:',
  'logMessageCount': 'Messages:',
  'logModel': 'Model:',
  'confirmRemovePeer': 'Confirm remove this peer? Related group shares will also be deleted.',
  'urlRequired': 'URL cannot be empty',
  'rotateFailed': 'Rotation failed',
  'savingToken': 'Saving Token...',
  'tokenSaveFailed': 'Token save failed',
  'verifyingRegister': 'Verifying public URL reachability and identity...',
  'registerFailed': 'Registration failed',
  'saveOrRegisterFailed': 'Save/Registration failed',
  'tokenUpdated': 'GitHub Token updated',
  'confirmRegenerateId': 'Confirm regenerate public ID? Old ID will be invalid. If registered on GitHub, re-registration is required.',
  'pleaseEnterToken': 'Please enter GitHub Token first',
  'tokenSavedEncrypted': 'GitHub Token saved encrypted',
  'connected': 'Connected',
  'connecting': 'Connecting',
  'failed': 'Failed',
  'connectFailed': 'Connection failed',
  'addPeerFailed': 'Failed to add peer',
  'deletePeerFailed': 'Failed to delete peer',
  'disconnectFailed': 'Failed to disconnect',
  'rotating': 'Rotating',
  'registerNotice': 'Register Public ID Notice',
  'tokenConfiguredClickChange': 'Token configured (click to change)',
  'pasteNewToken': 'Paste new token to replace old one',
  'tokenHintOrGet': 'Leave empty to use stored token. Get one:',
  'githubToken': 'GitHub Token',
  'tokenHelpText': 'Token required to write to registry. Click link -> Generate new token (classic) -> select repo',
  'saveTokenAndRegister': 'Save Token & Register',
  'knownAndContinue': 'I understand, continue',
  'instanceIdentity': 'Instance Identity',
  'displayName': 'Display Name',
  'instanceNamePlaceholder': 'My Instance',
  'publicUrlLabel': 'Public URL (enter domain or IP, /federation/ws auto-appended)',
  'publicIdLabel': 'Public ID (auto-generated ULID on startup, uniqueness ~1/4.3B)',
  'regenerate': 'Regenerate',
  'githubTokenLabel': 'GitHub Token (Get one →)',
  'tokenConfiguredPlaceholder': 'Configured (re-enter to overwrite)',
  'tokenPlaceholder': 'ghp_xxxxxxxxxxxxxxxx',
  'saveToken': 'Save Token',
  'tokenConfigured': 'Token configured (encrypted)',
  'saveInstanceInfo': 'Save Instance Info',
  'registering': 'Registering...',
  'registered': '✓ Registered',
  'registerToGithub': 'Register to GitHub',
  'saveAndRetry': 'Save & Retry',
  'instanceId': 'Instance ID',
  'publicId': 'Public ID (ULID auto-generated)',
  'notGenerated': 'Not generated',
  'publicUrl': 'Public URL',
  'notConfigured': 'Not configured',
  'change': 'Change',
  'configure': 'Configure',
  'tokenConfiguredEncrypted': 'Configured (encrypted)',
  'peers': 'Peers ({count})',
  'addPeer': 'Add',
  'peerPublicId': 'Public ID',
  'peerIdPlaceholder': 'Copree-xxxxxxxx',
  'peerDisplayName': 'Display Name',
  'peerNamePlaceholder': "e.g., Huahua's Instance",
  'peerWsUrl': 'WebSocket URL (enter ip:port or domain, /federation/ws auto-appended)',
  'optional': 'Optional',
  'peerUrlOptionalHint': 'Leave blank to auto-fetch from the GitHub registry. Can also stay blank if the peer can connect to you — one-way connection is enough for bidirectional communication.',
  'sharedSecret': 'Shared Secret',
  'secretPlaceholder': 'At least 8 characters',
  'addAndConnect': 'Add & Connect',
  'noPeers': 'No peers yet. Click "Add" to connect other Copree instances.',
  'rotateUrl': 'Rotate URL',
  'disconnect': 'Disconnect',
  'connect': 'Connect',
  'peerUrl': 'Public URL',
  'secretNoChange': 'Shared secret (leave empty to not change)',
  'newSecretPlaceholder': 'Enter new secret to replace old one',
  'rotateHint': 'URL rotation — old URL remains valid until new URL is tested successfully',
  'initiateRotate': 'Initiate Rotation',
  'urlRotated': 'URL rotated',
  'lastRotated': 'Last:',
  'federationGroups': 'Federated Groups',
  'federationGroupsDesc': 'In group settings, configure federation sharing per group. Messages in federated groups will auto-forward to selected peers.',
  'federationGroupsHint': 'Hint: Add and connect peers first, then enable federation sharing in group settings.',
  'federationEntities': 'Federated Entities ({count})',
  'federationEntitiesHint': 'List of federated entities registered via entity_announce protocol or shared by remote admin.',
  'noFederatedEntities': 'No federated entities yet. When a remote instance shares a group/DM, it will appear here automatically.',
  'confirmRemoveEntity': 'Confirm remove this federated entity? Related message forwarding will stop.',
  'federationDiscovery': 'Public Discovery (Optional)',
  'federationDiscoveryDesc': 'The GitHub registry is just a public "phonebook" so other instances can discover you. You can federate without registering — just need both sides to know each other\'s URL and shared secret.',
  'federationDiscoveryHint': 'If you want your instance to be searchable by others in the GitHub registry, configure a Token and click "Register to GitHub Public Directory" above.',
  'registerNoticeText': 'After registering your public ID to the GitHub registry:',
  'registerNoticeItem1': 'Your public ID, display name, and public URL will be publicly visible',
  'registerNoticeItem2': 'Other Copree instances can discover and attempt to connect to your instance',
  'registerNoticeItem3': 'Connection requests require both sides to configure a shared secret to complete the handshake',
  'registerNoticeItem4': 'You can update or delete your entry in the registry at any time',
  'registerNoticeItem5': 'Make sure the public URL points to your own instance. Do not impersonate others.',
  'registerNoticeVerify': 'The system will verify that your public URL actually points to a running Copree instance before registering.',
  'enablePeer': 'Enable',
  'disablePeer': 'Disable',
  'globalConfig': 'Global Config',
  'enableOpenCLI': 'Enable OpenCLI',
  'rateLimit': 'Default rate limit (calls/min)',
  'timeoutSeconds': 'Timeout (seconds)',
  'opencliAiWhitelist': 'AI OpenCLI Whitelist',
  'opencliAiWhitelistColId': 'ID',
  'opencliAiWhitelistColName': 'Name',
  'opencliAiWhitelistColOwner': 'Owner',
  'opencliAiWhitelistColEnabled': 'OpenCLI',
  'opencliAiWhitelistColRate': 'Rate Limit',
  'opencliAiWhitelistColAction': 'Actions',
  'presetCommands': 'Preset Commands (one-click for beginners)',
  'presetCommandsDesc': "Below are the most common commands for AI. Click Add to add to whitelist. Already added commands won't be duplicated.",
  'addAllPresets': 'Add All Presets',
  'allAdded': '✓ All Added',
  'manualAddCommand': 'Manual Add Command',
  'manualAddDesc': 'To add commands not in presets (e.g. other website integrations), enter manually. Exact mode: match exact command name. Regex mode: match command patterns.',
  'commandPattern': 'Command/Regex',
  'commandPatternPlaceholder': 'e.g. bilibili or gh .*',
  'regexMode': 'Regex',
  'description': 'Description',
  'addCmd': 'Add',
  'commandWhitelist': 'Command Whitelist',
  'cmdColPattern': 'Pattern',
  'cmdColType': 'Type',
  'cmdColDesc': 'Description',
  'cmdColStatus': 'Status',
  'cmdColAction': 'Actions',
  'regex': 'Regex',
  'exact': 'Exact',
  'noCommands': 'No commands in whitelist — click presets above to add quickly',
  'usageLogs': 'Usage Logs',
  'logsColAi': 'AI',
  'logsColCmd': 'Command',
  'logsColExitCode': 'Exit Code',
  'logsColDuration': 'Duration',
  'logsColOutput': 'Output Preview',
  'totalTokens': 'Total Tokens',
  'totalCalls': 'Total Calls',
  'activeAgents': 'Active AI',
  'activeUsers': 'Active Users',
  'dailyTokenConsumption': 'Daily Token Consumption',
  'usageByUser': 'Usage by User / AI',
  'aiCount': ' AI(s)',
  'callsCount': ' calls',
  'noData': 'No data',
  'aiDailyTokens': 'AI #{id} Daily Token Distribution',
  'thinking': 'Thinking',
  'cached': 'Cached',
  'cacheHitRate': 'Cache Hit Rate',
  'noAiDailyData': 'No daily data for this AI',
  'daysSuffix': 'd',
  'usageTabResidents': 'Resident AI',
  'usageTabWorlds': 'Group Worlds',
  'residentsDailyTokens': 'Resident AI Daily Token Consumption',
  'worldsUsage': 'Group World Usage',
  'worldsUsageEmpty': 'No group-world calls in this range',
  'worldsDailyTokens': 'Daily Group-World Token Consumption',
  'worldsActive': 'Worlds with Calls',
  'worldColWorld': 'World',
  'worldColOwner': 'Owner',
  'worldColLastAt': 'Last Call',
  'toolRegistry': 'Tool Registry',
  'skillManagement': 'AI Skill Mgmt',
  'toolsColName': 'Name',
  'toolsColDesc': 'Description',
  'toolsColSegment': 'Segment',
  'toolsColStates': 'States',
  'toolsFilterSegment': 'By Segment',
  'toolsFilterState': 'By State',
  'toolsFilterAll': 'All',
  'toolsTotal': '{n} tools',
  'toolsSegments': '{n} segments',
  'toolsParamSchema': 'Parameters',
  'skillsSelectAgent': 'Select AI',
  'skillsColName': 'Name',
  'skillsColType': 'Type',
  'skillsColEnabled': 'Enabled',
  'skillsColConfig': 'Config',
  'skillsColAction': 'Action',
  'skillsToggle': 'Toggle',
  'skillsNoAgent': 'Select an AI first',
  'skillsLoadFailed': 'Failed to load skills',
  'skillsToggleFailed': 'Toggle failed',
  'skillBackpack': 'Skill Backpack',
  'registrationEnabled': 'Open Registration',
  'registrationEnabledDesc': 'When disabled, new users cannot register via the signup page. Admins can still create accounts manually.',
  'createUser': 'Create User',
  'createUserTitle': 'Create User Manually',
  'createUserUsername': 'Username',
  'createUserPassword': 'Password',
  'createUserEmail': 'Email (optional)',
  'importCsv': 'Import CSV',
  'importCsvTitle': 'Bulk Import via CSV',
  'importCsvHint': 'CSV format: username,password[,email]. First row can be header.',
  'createUserUsernameHint': 'At least 2 characters',
  'createUserPasswordHint': 'At least 6 characters',
  'importCsvRowResult': 'Row {row}',
  'cleanup': 'File Cleanup',
  'cleanupDesc': 'Scan and clean unused avatar files and dead file references',
  'cleanupRun': 'Run Cleanup',
  'cleanupRunning': 'Cleaning...',
  'cleanupLastRun': 'Last Cleanup',
  'cleanupFilesDeleted': 'Files Cleaned',
  'cleanupOrphansDeleted': 'Dead References',
  'cleanupNever': 'Never Run',
}

export const adminJa: TranslationDict = {
  'title': '管理パネル',
  'subtitle': 'システム管理と監視 ·',
  'backToList': '一覧に戻る',
  'backToApp': 'アプリに戻る',
  'collapseRail': 'ナビを畳む',
  'expandRail': 'ナビを展開',
  'backToMe': 'マイに戻る',
  'groupGeneral': '一般',
  'groupPeople': 'ユーザーとコンテンツ',
  'groupCapabilities': '機能と連携',
  'groupMonitoring': '監視とログ',
  'groupData': 'データと保守',
  'capabilities': 'プラグインとスキル',
  'capabilitiesPlugins': 'プラグイン',
  'capabilitiesDesc': 'プラグイン・ツール・スキル・スキルバックパック',
  'prompt': 'システムプロンプト',
  'promptDesc': 'AIに送るシステムプロンプトの確認と編集',
  'store': 'マーケット',
  'storeDesc': 'プラグインマーケット・インストール済み・同期ソース（ワールドマーケットは一般公開のまま）',
  'overviewDesc': 'サイト規模とメンテナンスモード',
  'usersDesc': 'アカウント・ロール・管理者権限',
  'agentsDesc': '全AIキャラクターと自己編集スイッチ',
  'groupsDesc': 'グループ監査と解散',
  'codesDesc': '引換コードの生成と一覧',
  'apikeyPoolDesc': '共有APIキープールと呼び出し統計',
  'opencliDesc': 'CLIブリッジの設定・認可・ログ',
  'federationDesc': '他サイトとの連携とフェデレーション',
  'metricsDesc': 'CPU・メモリ・ディスクのリアルタイム監視',
  'usageDesc': 'モデル呼び出しとクォータ消費の分析',
  'convlogDesc': '全会話のメッセージ履歴検索',
  'auditDesc': '管理者操作の監査',
  'backupDesc': 'データベースとファイルのエクスポート・復元',
  'totalUsers': '総ユーザー数',
  'totalAgents': '総AI数',
  'totalGroups': '総グループ数',
  'total': '合計',
  'pendingRequests': '保留中の申請',
  'usersColId': 'ID',
  'usersColUsername': 'ユーザー名',
  'usersColRole': '役割',
  'usersColQuota': 'クォータ',
  'usersColStatus': '状態',
  'usersColAction': '操作',
  'active': '有効',
  'banned': '禁止中',
  'ban': '禁止',
  'unban': '解除',
  'confirmPromote': '{username} を管理者に昇格しますか？',
  'promote': '昇格',
  'confirmDemote': '{username} の管理者権限を剥奪しますか？',
  'demote': '降格',
  'resetPassword': 'パスワードをリセット',
  'resetPasswordTitle': '{username} のパスワードをリセット',
  'resetPasswordPlaceholder': '新しいパスワードを入力（6文字以上）',
  'agentsColId': 'ID',
  'agentsColName': '名前',
  'agentsColOwner': '所有者',
  'agentsColStatus': '状態',
  'agentsColSelfEdit': '自己編集',
  'agentsColAction': '操作',
  'searchAiPlaceholder': 'AI 名で検索...',
  'disableSelfEdit': '自己編集を無効化',
  'enableSelfEdit': '自己編集を有効化',
  'groupsColId': 'ID',
  'groupsColName': '名前',
  'groupsColOwner': 'グループオーナー',
  'groupsColVector': 'ベクター高速化',
  'groupsColAction': '操作',
  'enabled': '有効',
  'notEnabled': '無効',
  'disabled': '無効',
  'confirmDismissGroup': 'このグループを解散しますか？',
  'dismiss': '解散',
  'creditApi': 'APIクレジット',
  'creditBundle': 'AIパッケージ',
  'creditFile': 'ファイル容量',
  'generateFailed': '生成に失敗しました。パラメータを確認してください',
  'generateCode': 'コードを生成',
  'codeType': 'タイプ',
  'expiresDays': '有効日数',
  'isApiPool': 'APIプールクォータ',
  'generating': '生成中...',
  'generate': '生成',
  'codeNote': 'メモ（管理者用）',
  'codeNotePlaceholder': '例: 田中さん用APIクレジット',
  'maxUsageLabel': 'コードあたり最大使用回数（空=一度に全額）',
  'balanceHint': '1 クレジット = 10,000 トークン',
  'codeOneTimeWarning': 'このコードは一度しか表示されません。コピーして保管してください',
  'codeList': 'コード一覧',
  'codesColCode': 'コード',
  'codesColType': 'タイプ',
  'codesColAmount': '額',
  'codesColNote': 'メモ',
  'codesColExpires': '有効期限',
  'codesColStatus': '状態',
  'pool': 'プール',
  'codeTypeDefault': 'AI作成枠',
  'usedBy': '使用済み',
  'available': '利用可能',
  'enable': '有効',
  'disable': '無効',
  'delete': '削除',
  'save': '保存',
  'cancel': 'キャンセル',
  'saving': '保存中...',
  'saveSuccess': '保存成功',
  'saveFailed': '保存失敗',
  // システム設定／システムプロンプトタブの即時通知（従来は中国語直書き＋中文で語氣判定）
  'updateFailed': '更新に失敗しました',
  'uploadLimitUpdated': 'アップロードサイズ上限を更新しました',
  'avatarLimitUpdated': 'アバターサイズ上限を更新しました',
  'bulkConcurrencyConfirm': 'すべてのグループの AI 同時実行数を {n} に設定しますか？この操作は取り消せません。',
  'bulkConcurrencySet': 'すべてのグループの同時実行数を {n} に設定しました',
  'bulkUpdateFailed': '一括変更に失敗しました',
  'bulkApplyRunning': '実行中…',
  'bulkApply': '一括適用',
  'promptResetDone': '既定に戻しました',
  'promptResetFailed': 'リセットに失敗しました',
  'promptOrderSaved': '順序を保存しました',
  'promptOrderSaveFailed': '順序の保存に失敗しました',
  'allUsers': '全ユーザー',
  'operationFailed': '操作に失敗しました',
  'deleteFailed': '削除に失敗しました',
  'loadFailed': '読み込みに失敗しました',
  'downloadFailed': 'ダウンロードに失敗しました',
  'dbBackupSuccess': '✓ データベースバックアップをダウンロードしました',
  'fullBackupSuccess': '✓ 完全バックアップをダウンロードしました（データベース + 全ファイル）',
  'restoreFailed': '復元に失敗しました',
  'dbRestoreSuccess': '✓ データベースを復元しました。ページを再読み込みしてください',
  'dbRestoreRestart': '✓ データベースを復元しました。アプリケーションを再起動してください',
  'fullRestoreSuccess': '✓ 完全バックアップを復元しました：データベース + 全ファイル。ページを再読み込みしてください',
  'fullRestoreRestart': '✓ 完全バックアップを復元しました：データベース + 全ファイル。アプリケーションを再起動してください',
  'presetsAddComplete': 'プリセットコマンドを追加しました',
  'presetsAddFailed': 'プリセットの追加に失敗しました',
  'exportTitle': 'データエクスポート',
  'exportDesc': '2種類のバックアップ形式からお選びください。',
  'fullBackup': '完全バックアップ（推奨）',
  'fullBackupDesc': '.tar.gz 圧縮ファイルをエクスポート。全データベースデータと /app/data/ 以下の全ファイル（AIファイル、添付、アバター等）を含みます。完全な移行に利用可能。',
  'packing': 'パッキング中...',
  'downloadFullBackup': '完全バックアップをダウンロード',
  'dbOnly': 'データベースのみ',
  'dbOnlyDesc': '.sql ファイルをエクスポート。テーブル構造と内容のみ。サイズは小さいですが、アップロードされた添付やAIファイルは含まれません。',
  'exporting': 'エクスポート中...',
  'downloadDbOnly': 'データベースのみダウンロード',
  'restoreTitle': 'データ復元',
  'restoreWarning': '復元すると現在のデータはすべて上書きされます。バックアップファイルが正しいことを確認してください。',
  'currentDbBackend': '現在のデータベース',
  'backupFileExt': 'バックアップファイル拡張子',
  'fullRestore': '完全復元',
  'fullRestoreDesc': '.tar.gz 完全バックアップをアップロードし、データベース + 全ファイルを復元します。',
  'restoringFull': '完全復元中...',
  'dbRestore': 'データベース復元のみ',
  'dbRestoreDesc': '.sql ファイルをアップロードしてデータベースのみ復元します。',
  'restoring': '復元中...',
  'logsColTime': '時刻',
  'logsColType': '種別',
  'logsColOperator': '操作者',
  'logsColTarget': '対象',
  'logsColDetail': '詳細',
  'logsColResult': '結果',
  'logsResultOk': '成功',
  'logsResultFailed': '失敗',
  'logsAnonymous': '匿名',
  'logsFilterType': '種類',
  'logsFilterAll': 'すべて',
  'opsTab': '運用サマリー',
  'opsTabDesc': '1画面：ログイン・遮断・会話・新規ユーザー',
  'opsRangeLabel': '集計範囲',
  'opsRange1': '直近24時間',
  'opsRange7': '直近7日',
  'opsRange30': '直近30日',
  'opsLoginOk': 'ログイン成功',
  'opsLoginFail': 'ログイン失敗',
  'opsSuspects': '攻撃の疑いがある発信元',
  'opsBlocked': '遮断',
  'opsTurns': 'AI のターン数',
  'opsNewUsers': '新規ユーザー',
  'opsLoginOkValue': '{n} 回',
  'opsLoginFailValue': '{n} 回',
  'opsSuspectsValue': '{n} 件',
  'opsBlockedValue': '{n} 回',
  'opsTurnsValue': '{n} ターン',
  'opsNewUsersValue': '{n} 人',
  'opsSubAccounts': '{n} アカウント',
  'opsSubTop': '最多の発信元は {n} 回失敗',
  'opsNoSuspects': '不審な発信元なし',
  'opsSubFailLocked': 'うち {n} 回はしきい値に達してロック',
  'opsSubRepeat': '失敗の {p}% は繰り返し失敗している発信元（2 回以上）',
  'opsSubBlocked': 'ロック {a} 回 · レート制限 {b} 回',
  'opsSubBlockedPartial': 'ロック {a} 回 · レート制限 —（今回の起動以降 {b} 回）',
  'opsSubMessages': '{n} 件のメッセージ',
  'opsSubTotalUsers': '現在 {n} アカウント',
  'opsTipLoginOk': '口径：集計範囲内のログイン成功件数。出所：監査ログ system_logs の login。カードを押すと明細。',
  'opsTipLoginFail': '口径：実際にパスワードを間違えた回数（ロックで弾いた分は「遮断」側）。出所：system_logs の login_failed。',
  'opsTipSuspects': '「疑い」= 集計範囲内で {min} 回以上失敗した発信元 IP または対象アカウント。確認された攻撃ではありません。',
  'opsTipBlocked': '口径：ロックによる遮断（監査・永続）+ 認証レート制限（プロセス内カウント・再起動で 0）。括弧内は内訳。「繰り返し失敗の割合」は効果の目安です。',
  'opsTipTurns': '口径：AI が 1 回発言するごとに 1 ターン。出所：ai_conversation_logs、メッセージ数は同範囲のグループ＋DM。',
  'opsTipNewUsers': '口径：集計範囲内の登録成功件数。出所：system_logs の register。',
  'opsFootnote': '数値は監査ログとテーブル集計から算出。期間は UTC（現在：{window}）。レート制限の回数は今回の起動以降で、再起動すると 0 に戻り、「—」は期間の一部しかカバーしていないことを示します。「疑い」はしきい値に達した発信元で、確認された攻撃ではありません。',
  'homeAttention': '対応が必要',
  'homeAllClear': '対応が必要なものはありません',
  'homeGo': '開く',
  'homePending': '承認待ちの申請 {n} 件',
  'homePendingHint': 'フレンド申請・参加申請・招待の承認待ち',
  'homeSuspects': '攻撃が疑われる接続元 {n} 件',
  'homeSuspectsHint': '直近 24 時間で同一元が {min} 回以上失敗',
  'homeLockouts': '失敗が続き {n} 件ロック',
  'homeLockoutsHint': '失敗回数のしきい値に達した接続元を一時的に遮断',
  'homeNoPoolKey': 'API キープールに有効なキーがありません',
  'homeNoPoolKeyHint': 'AI がモデルを呼べません。キーを追加して有効化してください',
  'homeNoBackup': 'ローカルバックアップがまだありません',
  'homeNoBackupHint': '日次バックアップを有効にするか、手動で取得してください',
  'homeBackupStale': '最新のバックアップは {n} 日前',
  'homeBackupStaleHint': '自動バックアップが動いていない可能性があります',
  'homeMaintenanceOn': 'メンテナンスモード中',
  'homeMaintenanceOnHint': 'ユーザーには通知が表示されます。API の挙動はモードにより異なります',
  'homeNumbers': '現在の状況',
  'homeNumbersHint': '最初の 3 つは合計、残りは直近 24 時間。クリックで詳細',
  'homeRecent': '最近アクセス',
  'homeAllEntries': 'すべての入口',
  'homeAllEntriesHint': '左のナビは現在のワークスペースのみ。ここでは全体を一覧できます',
  'homeLast24h': '直近 24 時間',
  'systemSettings': 'プラットフォーム全体設定',
  'auth': '認証設定',
  'authDesc': 'SMTPメール設定、認証コードログイン',
  'smtpConfig': 'SMTP 設定',
  'smtpHost': 'SMTP ホスト',
  'smtpPort': 'ポート',
  'smtpUsername': 'ユーザー名',
  'smtpPassword': 'パスワード（認証コード）',
  'smtpPasswordPlaceholder': '変更しない場合は空欄',
  'smtpFromEmail': '送信元メール',
  'smtpFromName': '送信者名',
  'smtpUseTls': 'TLS を使用',
  'smtpTest': '接続テスト',
  'smtpTesting': 'テスト中...',
  'smtpTestSuccess': 'SMTP 接続成功',
  'smtpTestFailed': 'SMTP 接続失敗',
  'smtpNotConfigured': 'SMTP 未設定',
  'smtpAdd': '送信者を追加',
  'smtpDelete': '削除',
  'smtpActive': '有効',
  'smtpInactive': '無効',
  'smtpPriority': '優先度（小さいほど優先）',
  'smtpMinOne': '少なくとも1つのSMTP設定が必要です',
  'requireEmailVerification': 'メール認証を要求',
  'requireEmailVerificationDesc': 'オンにすると新規登録時にメール認証が必要になります。既存ユーザーには影響しません。',
  'requireEmailVerificationWarning': 'メール認証を有効にする前に、SMTPを設定してテストしてください',
  'loginProviders': 'ログイン方法',
  'loginProvidersDesc': '少なくとも1つの方法を残してください',
  'loginProviderDirect': 'ユーザー名+パスワード（直接）',
  'loginProviderEmail': 'メール+認証コード',
  'loginProviderWechat': 'WeChat ログイン',
  'loginProviderQQ': 'QQ ログイン',
  'loginProviderLastWarning': '最後のログイン方法を無効にすることはできません',
  'emailTemplates': 'メールテンプレート',
  'emailPresetGradient': 'グラデーション',
  'emailPresetSimple': 'シンプル',
  'emailPresetCustom': 'カスタム',
  'emailPresetGradientActive': 'グラデーションテンプレート使用中',
  'emailPresetSimpleActive': 'シンプルテンプレート使用中',
  'emailPresetHint': '「カスタム」を選択してメールHTMLを編集',
  'emailTemplateViewPreview': 'プレビューを見る',
  'emailTemplateSubject': '件名',
  'emailTemplateBody': 'HTML本文',
  'emailTemplateReset': 'デフォルトにリセット',
  'emailTemplateResetAll': 'すべてリセット',
  'emailTemplatePreview': 'プレビュー',
  'emailTemplateVariables': '利用可能な変数',
  'emailTemplateVarHint': '{変数名} 形式で使用します。利用可能: {code}, {from_name}, {username}, {purpose_label}, {instance_name}, {expire_minutes}',
  'emailTemplatesSave': 'テンプレートを保存',
  'emailTemplatesSaved': 'テンプレートを保存しました',
  'llmProvider': 'LLM プロバイダープリセット',
  'manualConfig': '手動設定',
  'defaultChatModel': 'デフォルトチャットモデル',
  'defaultWorkModel': 'デフォルト作業モデル',
  'modelOptionsJson': 'モデルオプション一覧 (JSON)',
  'addProvider': 'プロバイダーを追加',
  'noProvidersYet': 'API プロバイダーがまだ設定されていません',
  'providerName': 'プロバイダー名',
  'confirmDeleteProvider': 'プロバイダー「{name}」を削除しますか？',
  'thinkingSupported': '深い推論に対応',
  'setAsDefault': 'デフォルトに設定',
  'fetchModels': 'モデルを取得',
  'fetchModelsHint': 'APIキー（取得のみ・保存されません）',
  'fetchModelsOk': '{n} 件のモデルを取得しました',
  'fetchModelsEmpty': 'このプロバイダはモデル一覧を返しませんでした。手動で入力してください',
  'defaultProvider': 'デフォルトプロバイダー',
  'defaultLanguage': 'グローバルデフォルト言語',
  'defaultLanguageDesc': '未選択の新規ユーザーはこのデフォルト言語を使用します。言語を設定済みのユーザーには影響しません。',
  'defaultPlatformCredit': 'グローバルデフォルトプラットフォームクレジット',
  'defaultPlatformCreditDesc': '新規登録時に自動付与されるクレジット。変更すると差分で全ユーザーが一括更新されます。0 = 無効。有効なプールキーが最低1つ必要です。',
  'platformCreditNoActiveKey': 'APIキープールに有効なキーがありません。先にキーを追加して有効化してください。',
  'platformCreditConfirm': 'プラットフォームクレジットを変更しますか？\n\n現在 {old} → 新規 {new}\n差分 = {delta}\n\nこの操作は全 {userCount} ユーザーに影響します！',
  'defaultFileQuota': 'ユーザーデフォルトファイル容量 (MB)',
  'defaultFileQuotaDesc': '新規ユーザーの初期ストレージ容量。変更すると全ユーザーの基本容量が一括調整されます（交換コードによる上乗せは保持）。',
  'fileQuotaIncreaseConfirm': 'ファイル容量の基本値を増やしますか？\n\n現在 {old}MB → 新規 {new}MB\n全ユーザーに +{delta}MB 追加されます',
  'fileQuotaDecreaseConfirm': 'ファイル容量の基本値を減らしますか？\n\n現在 {old}MB → 新規 {new}MB\n全ユーザーの基本容量が {new}MB になります（交換コード分は保持）。\n\n⚠ 制限を超えているユーザーは新しいファイルをアップロードできなくなります。',
  'presets': 'プリセット',
  'panel': '管理パネル',
  'overview': '概要',
  'users': 'ユーザー',
  'agents': 'AI管理',
  'groups': 'グループ監査',
  'codes': 'コード',
  'opencli': 'OpenCLI',
  'logs': '会話ログ',
  'federation': '連合',
  'files': 'ファイル',
  'usage': '使用量',
  'apikeyPool': 'APIキープール',
  'backup': 'バックアップ',
  'audit': '監査',
  'systemMetrics': 'システムメトリクス',
  'system': 'プラットフォーム設定',
  'systemDesc': 'グローバルデフォルト言語など',
  'systemCategory': 'システム設定',
  'maintenanceMode': 'メンテナンスモード',
  'maintenanceStatusPaused': 'サービス停止中',
  'maintenanceStatusTip': 'メンテナンス通知',
  'maintenanceStatusNormal': '正常',
  'maintenanceResumeService': 'サービス再開',
  'maintenancePauseService': 'サービス停止',
  'maintenanceCancelTip': '通知キャンセル',
  'maintenanceShowTip': '通知表示',
  'serverStatus': 'サーバーステータス',
  'maintenanceStarting': '起動中',
  'maintenanceRunning': '· 稼働中 ·',
  'maintenanceStopped': 'サービス停止中',
  'maintenanceTipLabel': '· 通知中 ·',
  'maintenanceDescAuto': 'バックエンド起動/停止中 — 自動一時停止',
  'maintenanceDescHard': '管理者がサービスを停止しました',
  'maintenanceDescSoft': '管理者がメンテナンス通知を有効にしました',
  'maintenanceDescNormal': 'サービスは正常に稼働中',
  'broadcast': '全プラットフォーム通知',
  'confirmDeletePoolKey': 'プールキー「{name}」を削除しますか？このキーに紐づくユーザーは自動的に解除されます。',
  'apiKeyPool': 'APIキープール',
  'apiKeyPoolDesc': 'システムレベルの共有APIキーを管理。ユーザーがクレジットを交換するとプールから自動割り当てされます。',
  'editKey': '編集',
  'testKey': '接続テスト',
  'testing': 'テスト中',
  'testOk': '接続OK',
  'testFailed': 'テスト失敗',
  'testModels': '利用可能なモデル {n} 件',
  'editApiKeyModal': 'プールキーを編集',
  'keyApiKeyKeepHint': '空欄なら現在のキーを保持',
  'addKey': 'キーを追加',
  'noApiKeys': 'APIキーがありません',
  'noKeysDesc': 'システム共有APIキーを追加すると、ユーザーはクレジット交換後に自動利用できるようになります。',
  'addFirstKey': '最初のキーを追加',
  'keyColName': '名前',
  'keyColApiUrl': 'APIアドレス',
  'keyColKey': 'キー',
  'keyColPriority': '優先度',
  'keyColStats': '統計',
  'keyColConcurrent': '同時実行',
  'keyColStatus': '状態',
  'keyColAction': '操作',
  'keyEncryptedHint': 'キーは暗号化され、末尾4文字のみ表示されます',
  'clickToDisable': 'クリックで無効化',
  'clickToEnable': 'クリックで有効化',
  'addApiKeyModal': 'APIキー追加',
  'addKeyEncryptNote': 'キーは Fernet 暗号化で保存され、追加後は平文を見ることはできません。',
  'keyNameRequired': '名前 *',
  'keyNamePlaceholder': '例: DeepSeek メインアカウント',
  'keyApiBaseUrl': 'API Base URL',
  'keyApiUrlPlaceholder': '空欄でグローバル設定を継承',
  'keyApiKey': 'API Key *',
  'keyPriorityLabel': '優先度（高いほど優先割り当て）',
  'keyConcurrentLimit': '同時実行上限',
  'keyConcurrentLimitPlaceholder': '空=モデルデフォルト',
  'keyConcurrentDefault': 'デフォルト',
  'keyModels': '利用可能なモデル（カンマ区切り）',
  'keyModelsPlaceholder': '例：deepseek-flash, deepseek-v4-pro',
  'keyModelsHint': '空欄なら関連プロバイダーのプリセットを使用。「テスト」で検出したモデル一覧を保存します',
  'keyDefaultModel': '既定モデル',
  'keyDefaultModelAuto': '自動（一覧の先頭）',
  'keyModelsSummary': '{n} 件 · 既定 {model}',
  'keyModelsUnset': '未登録（プロバイダープリセット）',
  'testModelsSaved': 'モデル一覧をこのキーに保存しました',
  'viewKeyStats': '使用統計を表示',
  'keyStatsTitle': '{name} 使用統計',
  'lastNDays': '直近 {n} 日',
  'keyStatsTotalReqs': '総リクエスト数',
  'keyStatsTotalTokens': '総トークン',
  'keyStatsTotalCredit': '総消費クレジット',
  'keyStatsActiveUsers': 'アクティブユーザー',
  'keyStatsTokenTrend': 'トークン消費トレンド',
  'keyStatsModelDist': 'モデル分布',
  'adding': '追加中...',
  'add': '追加',
  'noMetrics': 'まだ監視データがありません（最初の60秒フラッシュサイクル待ち）',
  'metricsRetention': '保存期間 {retentionDays} 日 · 60秒ごとに更新',
  'metricsLlmCalls': 'LLM呼び出し回数',
  'metricsAvgLatency': '平均遅延',
  'metricsErrorRate': 'エラー率',
  'metricsMaxQueueDepth': '最大キュー深度',
  'metricsLatencyTrend': 'LLM遅延トレンド',
  'metricsAvgLatencyS': '平均遅延 (s)',
  'metricsMsgPerSec': 'メッセージ/秒',
  'metricsErrorDistribution': 'エラー分布',
  'metricsToolStats': 'ツール呼び出し統計',
  'toolColName': 'ツール',
  'toolColCalls': '呼び出し',
  'toolColAvg': '平均',
  'toolColSuccessRate': '成功率',
  'willingnessDistribution': '意思スコア分布',
  'convlogGlobal': '全体設定',
  'convlogPerAgent': 'AI別設定',
  'convlogViewer': 'ログを表示',
  'convlogConfigTitle': '全体会話ログ設定',
  'convlogHardLimit': 'アクティブ状態の保持数（3日以内に動いた会話）',
  'convlogDefaultLimit': '新規ユーザーのデフォルト保持数（≤ システム上限）',
  'convlogIdleKeep': '沈静状態の保持数（3日以上動きなし）',
  'convlogAgedKeep': '古い状態の保持数（30日以上動きなし）',
  'convlogIdleDays': '沈静とみなす日数',
  'convlogAgedDays': '古いとみなす日数',
  'convlogKnobDefault': '空欄 = 既定値',
  'convlogDefaultAccess': 'ユーザーにAI会話ログの閲覧をデフォルト許可',
  'convlogDefaultDelay': '新規AIのデフォルト遅延返信有効',
  'convlogDefaultDelayDesc': 'オフの場合、新規作成AIは手動で遅延返信スキルを有効にする必要があります',
  'convlogCompressThreshold': 'コンテキスト圧縮しきい値',
  'convlogCompressThresholdDesc': 'コンテキストがウィンドウのこの割合に達したら自動圧縮。低い=早く圧縮、高い=より多く溜めてから圧縮',
  'convlogIdleThreshold': 'アイドル圧縮の係数',
  'convlogIdleThresholdDesc': '圧縮後の上限と圧縮しきい値の間で、アイドル時のしきい値をどこに置くか（既定 1/e ≈ 37%）。大きいほど圧縮しきい値に近づく',
  'convlogTargetPercent': '圧縮後の上限',
  'convlogTargetPercentDesc': '圧縮後の要約が圧縮しきい値に占める割合（既定 20%）。アイドルしきい値の下限にもなる',
  'saveConfig': '設定を保存',
  'perAgentSettings': 'AI別設定',
  'selectAi': 'AIを選択',
  'selectAiPlaceholder': '-- AIを選択 --',
  'currentEffective': '現在の設定：保持 <b>{retention}</b> 件 · ユーザーアクセス <b>{access}</b>',
  'retentionLimit': '保持上限（空欄=システム上限 {max}）',
  'allowUserViewLogs': 'ユーザーにログ表示を許可',
  'defaultOn': '全体デフォルトON',
  'defaultOff': '全体デフォルトOFF',
  'saveSettings': '設定を保存',
  'groupChat': 'グループ',
  'directMessage': 'DM',
  'hasOutput': '出力あり',
  'deepReasoning': '深い推論',
  'messages': ' メッセージ',
  'tokens': 'トークン:',
  'conversationLog': '会話ログ #{id}',
  'logType': '種別:',
  'logMessageCount': 'メッセージ数:',
  'logModel': 'モデル:',
  'confirmRemovePeer': 'このピアを削除しますか？関連するグループ共有も削除されます。',
  'urlRequired': 'URLは必須です',
  'rotateFailed': 'ローテーションに失敗しました',
  'savingToken': 'トークンを保存中...',
  'tokenSaveFailed': 'トークンの保存に失敗しました',
  'verifyingRegister': '公開URLの到達性と身元を確認中...',
  'registerFailed': '登録に失敗しました',
  'saveOrRegisterFailed': '保存/登録に失敗しました',
  'tokenUpdated': 'GitHubトークンを更新しました',
  'confirmRegenerateId': '公開IDを再生成しますか？古いIDは無効になります。GitHubに登録済みの場合は再登録が必要です。',
  'pleaseEnterToken': '先にGitHubトークンを入力してください',
  'tokenSavedEncrypted': 'GitHubトークンを暗号化保存しました',
  'connected': '接続済み',
  'connecting': '接続中',
  'failed': '失敗',
  'connectFailed': '接続に失敗しました',
  'addPeerFailed': 'ピアの追加に失敗しました',
  'deletePeerFailed': 'ピアの削除に失敗しました',
  'disconnectFailed': '切断に失敗しました',
  'rotating': 'ローテーション中',
  'registerNotice': '公開ID登録について',
  'tokenConfiguredClickChange': 'トークン設定済み（クリックで変更）',
  'pasteNewToken': '新しいトークンを貼り付けて置き換え',
  'tokenHintOrGet': '空欄の場合は保存済みトークンを使用。取得:',
  'githubToken': 'GitHub トークン',
  'tokenHelpText': 'レジストリへの書き込みにトークンが必要です。リンク → Generate new token (classic) → repo にチェック',
  'saveTokenAndRegister': 'トークンを保存して登録',
  'knownAndContinue': '理解しました。続行します',
  'instanceIdentity': 'インスタンス情報',
  'displayName': '表示名',
  'instanceNamePlaceholder': '私のインスタンス',
  'publicUrlLabel': '公開URL（ドメインまたはIPを入力、/federation/ws が自動付加）',
  'publicIdLabel': '公開ID（起動時に自動生成ULID、一意性 約43億分の1）',
  'regenerate': '再生成',
  'githubTokenLabel': 'GitHubトークン（取得 →）',
  'tokenConfiguredPlaceholder': '設定済み（再入力で上書き）',
  'tokenPlaceholder': 'ghp_xxxxxxxxxxxxxxxx',
  'saveToken': 'トークンを保存',
  'tokenConfigured': 'トークン設定済み（暗号化）',
  'saveInstanceInfo': 'インスタンス情報を保存',
  'registering': '登録中...',
  'registered': '✓ 登録済み',
  'registerToGithub': 'GitHubに登録',
  'saveAndRetry': '保存して再試行',
  'instanceId': 'インスタンスID',
  'publicId': '公開ID（ULID自動生成）',
  'notGenerated': '未生成',
  'publicUrl': '公開URL',
  'notConfigured': '未設定',
  'change': '変更',
  'configure': '設定',
  'tokenConfiguredEncrypted': '設定済み（暗号化）',
  'peers': 'ピア ({count})',
  'addPeer': '追加',
  'peerPublicId': '公開ID',
  'peerIdPlaceholder': 'Copree-xxxxxxxx',
  'peerDisplayName': '表示名',
  'peerNamePlaceholder': '例: ハナのインスタンス',
  'peerWsUrl': 'WebSocket URL（IP:ポートまたはドメイン、/federation/ws 自動付加）',
  'optional': '任意',
  'peerUrlOptionalHint': '空欄の場合、GitHubレジストリから自動取得します。相手が接続可能であれば空欄でも構いません（片方向接続で双方向通信可能）。',
  'sharedSecret': '共有秘密鍵',
  'secretPlaceholder': '8文字以上',
  'addAndConnect': '追加して接続',
  'noPeers': 'まだピアがありません。「追加」をクリックして他のCopreeインスタンスと接続してください。',
  'rotateUrl': 'URLローテーション',
  'disconnect': '切断',
  'connect': '接続',
  'peerUrl': '公開URL',
  'secretNoChange': '共有秘密鍵（変更しない場合は空欄）',
  'newSecretPlaceholder': '新しい秘密鍵を入力して置き換え',
  'rotateHint': 'URLローテーション — 新しいURLのテスト成功後に古いURLが無効になります',
  'initiateRotate': 'ローテーション開始',
  'urlRotated': 'URLがローテーションされました',
  'lastRotated': '最終:',
  'federationGroups': '連合グループ',
  'federationGroupsDesc': 'グループ設定パネルで各グループの連合共有を設定します。連合グループのメッセージは選択したピアに自動転送されます。',
  'federationGroupsHint': 'ヒント：先に「ピア」で他のインスタンスを追加・接続し、その後グループ設定で連合共有を有効にしてください。',
  'federationEntities': '連合エンティティ ({count})',
  'federationEntitiesHint': 'entity_announce プロトコルで登録された、またはリモート管理者が共有した連合エンティティの一覧です。',
  'noFederatedEntities': 'まだ連合エンティティはありません。リモートインスタンスがグループ/DMを共有すると自動的にここに表示されます。',
  'confirmRemoveEntity': 'この連合エンティティを削除しますか？関連するメッセージ転送が停止します。',
  'federationDiscovery': '公開ディスカバリ（任意）',
  'federationDiscoveryDesc': 'GitHubレジストリは公開「電話帳」にすぎず、他のインスタンスにあなたを発見してもらうためのものです。登録しなくても連合通信は可能です。双方が互いのURLと共有秘密鍵を知っていれば直接接続できます。',
  'federationDiscoveryHint': 'あなたのインスタンスをGitHubレジストリで検索可能にしたい場合は、トークンを設定して上部の「GitHub公開ディレクトリに登録」をクリックしてください。',
  'registerNoticeText': '公開IDをGitHubレジストリに登録すると：',
  'registerNoticeItem1': 'あなたの公開ID、表示名、公開URLが公開されます',
  'registerNoticeItem2': '他のCopreeインスタンスがレジストリを通じてあなたのインスタンスを発見し、接続を試みることができます',
  'registerNoticeItem3': '接続要求には、双方が共有秘密鍵を設定してハンドシェイクを完了する必要があります',
  'registerNoticeItem4': 'いつでもレジストリのエントリを更新または削除できます',
  'registerNoticeItem5': '公開URLがあなた自身のインスタンスを指していることを確認してください。他人のアドレスを詐称しないでください。',
  'registerNoticeVerify': '登録前に、システムがあなたの公開URLが実際に動作中のCopreeインスタンスを指していることを検証します。',
  'enablePeer': '有効',
  'disablePeer': '無効',
  'globalConfig': '全体設定',
  'enableOpenCLI': 'OpenCLI を有効にする',
  'rateLimit': 'デフォルトのレート制限（回/分）',
  'timeoutSeconds': 'タイムアウト（秒）',
  'opencliAiWhitelist': 'AI OpenCLI ホワイトリスト',
  'opencliAiWhitelistColId': 'ID',
  'opencliAiWhitelistColName': '名前',
  'opencliAiWhitelistColOwner': '所有者',
  'opencliAiWhitelistColEnabled': 'OpenCLI',
  'opencliAiWhitelistColRate': 'レート制限',
  'opencliAiWhitelistColAction': '操作',
  'presetCommands': 'プリセットコマンド（初心者向けワンクリック追加）',
  'presetCommandsDesc': 'AIがよく使うコマンドです。「追加」をクリックするとホワイトリストに追加されます。既に追加済みのものは重複しません。',
  'addAllPresets': 'すべてのプリセットを一括追加',
  'allAdded': '✓ すべて追加済み',
  'manualAddCommand': 'コマンドを手動追加',
  'manualAddDesc': 'プリセットにないコマンド（他のWebサイト連携など）を追加するには、ここで手動入力します。完全一致モード：正確なコマンド名に一致。正規表現モード：コマンドパターンに一致。',
  'commandPattern': 'コマンド/正規表現',
  'commandPatternPlaceholder': 'bilibili または gh .*',
  'regexMode': '正規表現',
  'description': '説明',
  'addCmd': '追加',
  'commandWhitelist': 'コマンドホワイトリスト',
  'cmdColPattern': 'パターン',
  'cmdColType': 'タイプ',
  'cmdColDesc': '説明',
  'cmdColStatus': '状態',
  'cmdColAction': '操作',
  'regex': '正規表現',
  'exact': '完全一致',
  'noCommands': 'ホワイトリストにコマンドがありません — 上部のプリセットカードをクリックして追加してください',
  'usageLogs': '使用ログ',
  'logsColAi': 'AI',
  'logsColCmd': 'コマンド',
  'logsColExitCode': '終了コード',
  'logsColDuration': '所要時間',
  'logsColOutput': '出力プレビュー',
  'totalTokens': '総トークン',
  'totalCalls': '総呼び出し数',
  'activeAgents': 'アクティブAI',
  'activeUsers': 'アクティブユーザー',
  'dailyTokenConsumption': 'サイト全体の日次トークン消費',
  'usageByUser': 'ユーザー別・AI使用量明細',
  'aiCount': ' AI',
  'callsCount': ' 回の呼び出し',
  'noData': 'データなし',
  'aiDailyTokens': 'AI #{id} 日次トークン分布',
  'thinking': '思考',
  'cached': 'キャッシュ',
  'cacheHitRate': 'キャッシュヒット率',
  'noAiDailyData': 'このAIの日次データはありません',
  'daysSuffix': '日',
  'usageTabResidents': '住民AI',
  'usageTabWorlds': 'グループワールド',
  'residentsDailyTokens': '住民AI日次トークン消費',
  'worldsUsage': 'グループワールド使用量',
  'worldsUsageEmpty': 'この期間のグループワールド呼び出しはありません',
  'worldsDailyTokens': 'グループワールド日次トークン消費',
  'worldsActive': '呼び出しのあるワールド',
  'worldColWorld': 'ワールド',
  'worldColOwner': 'オーナー',
  'worldColLastAt': '最終呼び出し',
  'toolRegistry': 'ツールレジストリ',
  'skillManagement': 'AIスキル管理',
  'toolsColName': '名前',
  'toolsColDesc': '説明',
  'toolsColSegment': 'セグメント',
  'toolsColStates': '対応状態',
  'toolsFilterSegment': 'セグメントで絞込',
  'toolsFilterState': '状態で絞込',
  'toolsFilterAll': 'すべて',
  'toolsTotal': '{n} ツール',
  'toolsSegments': '{n} セグメント',
  'toolsParamSchema': 'パラメータスキーマ',
  'skillsSelectAgent': 'AIを選択',
  'skillsColName': 'スキル名',
  'skillsColType': 'タイプ',
  'skillsColEnabled': '有効',
  'skillsColConfig': '設定',
  'skillsColAction': '操作',
  'skillsToggle': '切替',
  'skillsNoAgent': '先にAIを選択してください',
  'skillsLoadFailed': 'スキルの読み込みに失敗しました',
  'skillsToggleFailed': '切り替えに失敗しました',
  'skillBackpack': 'スキルバックパック',
  'registrationEnabled': '新規登録を開放',
  'registrationEnabledDesc': '無効にすると新規ユーザーは登録ページからアカウントを作成できません。管理者は手動で作成できます。',
  'createUser': 'ユーザーを作成',
  'createUserTitle': 'ユーザーを手動で作成',
  'createUserUsername': 'ユーザー名',
  'createUserPassword': 'パスワード',
  'createUserEmail': 'メールアドレス（任意）',
  'importCsv': 'CSV をインポート',
  'importCsvTitle': 'CSV でユーザーを一括インポート',
  'importCsvHint': 'CSV 形式：username,password[,email]。1行目はヘッダーでも可。',
  'createUserUsernameHint': '2文字以上',
  'createUserPasswordHint': '6文字以上',
  'importCsvRowResult': '{row} 行目',
  'cleanup': 'ファイルクリーンアップ',
  'cleanupDesc': '未使用のアバターファイルと無効なファイル参照をスキャンしてクリーンアップ',
  'cleanupRun': 'クリーンアップを実行',
  'cleanupRunning': 'クリーンアップ中...',
  'cleanupLastRun': '前回のクリーンアップ',
  'cleanupFilesDeleted': '削除したファイル',
  'cleanupOrphansDeleted': '無効な参照',
  'cleanupNever': '未実行',
}
